/* final page media */

// ─── Top-selling SKUs for banner logic ────────────────────────────────────
const FINAL_RING_TOP_SELLING_SKUS = new Set(['83232','85292','85164','85345','85288','51163','50804','85185','85112','85264','50078','50204','85131','51162','83495','84961','51182','51120','50001','84843','50843','50975','85346','85230','84275','51057']);
// ──────────────────────────────────────────────────────────────────────────

// ─── Live 3D embed URL — shared by the "360° Setting" media tile below and
// FinalRingMetaData.sealRing3dUrl (the cart-line property builder). Same
// params/format as the live embed on the product page
// (snippets/product-3d-preview.liquid: currentRing3dUrl/buildSelection). ────
function buildRing3dEmbedUrl({ sku, shape, carat, metal, prongType, gem }) {
  try {
    var band = (sku || "").split("-")[0];
    if (!band) return null;
    var cfg = (window.RB_3D_CONFIG || {});
    var baseUrl = cfg.baseUrl || "https://i3dstudio.vercel.app";
    var embedId = cfg.embedId || "";
    // No embed id configured = 3D is off; callers fall back to the photo media.
    if (!embedId) return null;
    // Mirrors metalToken() in product-3d-preview.liquid: carat doesn't change
    // the 3D model, only colour does, so every karat maps to the 18k token;
    // platinum maps to its own preset.
    var metalStr = (metal || "").toLowerCase();
    var metalToken;
    if (metalStr.indexOf("platinum") !== -1 || metalStr.indexOf("plat") !== -1) {
      metalToken = "platinum_950";
    } else {
      var color = metalStr.indexOf("white") !== -1 ? "white" : (metalStr.indexOf("rose") !== -1 ? "rose" : "yellow");
      metalToken = color + "_gold_18k";
    }
    var head = { shape: shape || "Round", "prong-style": (prongType || "round").toLowerCase() };
    var caratNum = Number(carat);
    if (isFinite(caratNum) && caratNum > 0) head.carat = caratNum;
    return {
      // Render behaviour only — never the ring. This is a LIVE iframe hosted
      // on this same page, so (like product-3d-preview.liquid's hero) it
      // never needs the selection in its own address: the caller messages it
      // in over postMessage once the embed announces 'ready' instead (see
      // syncEngravingToHero(), which reads `selection` below rather than
      // re-parsing it back out of the src the way this used to work).
      url: baseUrl + "/embed/" + embedId + "?mode=view&controls=1",
      // Merchant-configured per-setting override (product.metafields.custom.ring_3d_gem,
      // fetched in getSettingData/buildSettingFromVariant) — null falls back to the
      // embed's own dashboard default, same contract as product-3d-preview.liquid.
      selection: { band: band, head: head, metal: metalToken, gem: gem || null },
    };
  } catch (e) {
    return null;
  }
}
// ──────────────────────────────────────────────────────────────────────────

// ─── Setting variant in the diamond's shape ────────────────────────────────
// A setting's variants are metal × shape (option1 × option2); the ring is made
// with a head for that shape, and the variant is what the cart receives. When
// the shopper's diamond is another shape (a Round setting paired with a Cushion
// stone via the product page's shape-mismatch "Continue", or a diamond changed
// after the setting), the 3D hero already renders the diamond's shape — the
// page's setting, and so the cart line, has to follow. This picks the sibling
// variant (same metal, the diamond's shape) from the product's variant list;
// FinalRingDetailPageComp.syncSettingVariantToDiamondShape() fetches the list
// and swaps the setting over.
// Same spelling rules as the product page's normalizeShape()
// (snippets/ring-builder-choose-setting-button.liquid): the diamond feed says
// "Trilliant" for the cut the variants call "Trillion".
const SETTING_SHAPE_ALIASES = { trilliant: "trillion" };
function normalizeShapeKey(shape) {
  var key = String(shape || "").trim().toLowerCase();
  return SETTING_SHAPE_ALIASES[key] || key;
}
function pickSettingVariantForShape(variants, currentId, metal, shapeKey) {
  if (!Array.isArray(variants) || !variants.length || !shapeKey) return null;
  var metalKey = String(metal || "").trim().toLowerCase();
  if (!metalKey) {
    var current = variants.find(function (v) { return String(v.id) === String(currentId); });
    metalKey = current ? String(current.option1 || "").trim().toLowerCase() : "";
  }
  var sameShape = variants.filter(function (v) { return normalizeShapeKey(v.option2) === shapeKey; });
  if (!sameShape.length) return null;
  var match = sameShape.find(function (v) {
    return String(v.option1 || "").trim().toLowerCase() === metalKey;
  }) || null;
  // The stored metal ("9K White") normally IS the option value; tolerate a
  // looser spelling before giving up — but never fall through to another
  // metal: a yellow-gold ring on a white-gold order is worse than the shape.
  if (!match && metalKey) {
    var tokens = metalKey.split(/\s+/).filter(Boolean);
    match = sameShape.find(function (v) {
      var option = String(v.option1 || "").toLowerCase();
      return tokens.every(function (t) { return option.indexOf(t) !== -1; });
    }) || null;
  }
  if (!match || String(match.id) === String(currentId)) return null;
  return match;
}
// ──────────────────────────────────────────────────────────────────────────

const FinalRingMediaGallery = {
  props: [
    "changeInMetalLoading",
    "mediaGallery",
    "placeholderShape",
    "diamond",
    // Derived by the parent and handed down here — syncEngravingToHero() posts
    // it into the hero iframe once ready.
    "frgHeroSelection",
    // The selected variant's photograph, shown over the 360° hero until the
    // embed reports it has painted the ring.
    "heroPoster",
  ],
  emits: ["imageError"],
  delimiters: ["[[", "]]"],
  template: `#final-ring-media-gallery`,
  data() {
    return {
      gallery: { pos: 0 },
      galleryImages: [],
      GIAImage: `${baseAssetsUrl}/gia.png`,
      IGIImage: `${baseAssetsUrl}/igi.webp`,
      drag: { active: false, startX: 0, startTime: 0, videoIdx: null, wasDragging: false },
      isMobile: false,
      mobileActive: 0,
      lightbox: { open: false, index: 0 },
      // ---- Diamond Try-On state ----
      tryonHandIndex: 0,
      tryonCarat: 0.5,
      tryonContainerWidth: typeof window !== "undefined" ? window.innerWidth : 1024,
      tryonHands: (typeof window !== "undefined" && window.__TRYON_ASSETS && window.__TRYON_ASSETS.hands) || [
        `${baseAssetsUrl}/hand1.jpg`,
        `${baseAssetsUrl}/hand2.jpg`,
        `${baseAssetsUrl}/hand3.jpg`,
      ],
      tryonRingImg: (typeof window !== "undefined" && window.__TRYON_ASSETS && window.__TRYON_ASSETS.ring) || `${baseAssetsUrl}/Ring.png`,
      tryonShapes: (typeof window !== "undefined" && window.__TRYON_ASSETS && window.__TRYON_ASSETS.shapes) || {
        round: `${baseAssetsUrl}/Round.jpg`,
        oval: `${baseAssetsUrl}/Oval.jpg`,
        cushion: `${baseAssetsUrl}/Cushion.jpg`,
        princess: `${baseAssetsUrl}/Princess.jpg`,
        radiant: `${baseAssetsUrl}/Radiant.jpg`,
        pear: `${baseAssetsUrl}/Pear.jpg`,
        marquise: `${baseAssetsUrl}/Marquise.jpg`,
        emerald: `${baseAssetsUrl}/Emerald.jpg`,
        asscher: `${baseAssetsUrl}/Asscher.jpg`,
        heart: `${baseAssetsUrl}/Heart.jpg`,
      },
      tryonCaratPresets: [1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0, 5.5, 6.0, 6.5, 7.0],
      // Live-sync state for the "360° Setting" hero iframe — mirrors
      // product-3d-preview.liquid's postMessage protocol so typing an engraving
      // here updates the same embed the same way, instead of reloading the
      // iframe on every keystroke via its `eng` URL param (which the embed does
      // support — see sealRing3dUrl — but a live iframe should be messaged,
      // not re-sourced).
      // Readiness itself lives on the iframe element (__frgReady), not here —
      // see onHeroEmbedMessage for why.
      _engravingSyncTimer: null,
      // Latches true the first time the embed reports it has actually painted
      // the ring ('view-ready'), which drops the variant-photo poster. Never
      // reset: the hero's src is render-only and constant, so the iframe is
      // never reloaded by a selection change and the ring can't un-paint.
      heroPainted: false,
    };
  },
  computed: {
    allMobileItems() {
      var items = [];
      var self = this;
      // Follow galleryImages order (already ordered by handleMediaGallery)
      this.galleryImages.forEach(function(item, idx) {
        if (item.type === 'certificate') {
          items.push({ srcType: 'cert', name: item.name, badge: item.badge || null, diamondData: item.diamondData, galIdx: idx });
        } else if (item.type === 'video') {
          items.push({ srcType: 'video', src: item.full, name: item.name, badge: item.badge || null, galIdx: idx });
        } else if (item.type === 'tryon') {
          items.push({ srcType: 'tryon', name: item.name || 'Try-On', galIdx: idx });
        } else {
          items.push({ srcType: 'image', src: item.full, thumbnail: item.thumbnail, name: item.name, badge: item.badge || null, link: item.link || null, galIdx: idx });
        }
      });
      return items;
    },
    activeMobileItem() {
      return this.allMobileItems[this.mobileActive] || this.allMobileItems[0];
    },
    // ---- Try-On computed ----
    tryonYoursCarat() {
      return Number(this.diamond && this.diamond.caratWeight) || 0;
    },
    tryonCurrentShape() {
      var s = (this.diamond && this.diamond.shape) || "round";
      return String(s).toLowerCase();
    },
    tryonShapeImg() {
      return this.tryonShapes[this.tryonCurrentShape] || this.tryonShapes.round;
    },
    tryonHandImg() {
      return this.tryonHands[this.tryonHandIndex] || this.tryonHands[0];
    },
    tryonDiamondSize() {
      var carat = Math.max(this.tryonCarat, 0.1);
      var mmSize = 5.5 * Math.cbrt(carat);
      var PX_PER_MM;
      if (this.tryonContainerWidth <= 768) {
        var handStageWidth = Math.max(this.tryonContainerWidth - 70, 180);
        PX_PER_MM = Math.min((handStageWidth * 0.16) / 17, 3.0);
        if (this.tryonCurrentShape === "marquise") PX_PER_MM *= 1.2;
      } else {
        PX_PER_MM = this.tryonCurrentShape === "marquise" ? 4.5 : 2.6;
        if (this.tryonContainerWidth <= 1280) PX_PER_MM *= 0.9;
        if (this.tryonContainerWidth <= 1024) PX_PER_MM *= 0.85;
      }
      return mmSize * PX_PER_MM;
    },
    tryonDisplayMm() {
      var carat = Math.max(this.tryonCarat, 0.1);
      return (6.5 * Math.cbrt(carat)).toFixed(1);
    },
    isTryonYoursSelected() {
      var y = this.tryonYoursCarat;
      return y > 0 && Math.abs(this.tryonCarat - y) < 0.05;
    },
  },
  mounted() {
    this.isMobile = window.innerWidth <= 768;
    window.addEventListener('resize', this.checkMobile);
    window.addEventListener('resize', this.onTryonResize);
    window.addEventListener('keydown', this._lbKeydown);
    this.galleryImages = [...this.mediaGallery];
    // Initialise Try-On carat from current diamond
    var y = Number(this.diamond && this.diamond.caratWeight) || 0;
    if (y > 0) this.tryonCarat = y;

    // Ensure all videos autoplay
    this.playAllVideos();

    // Global move/up listeners for drag-to-scrub
    window.addEventListener('mousemove', this.onDrag);
    window.addEventListener('mouseup', this.stopDrag);
    window.addEventListener('touchmove', this.onDrag, { passive: false });
    window.addEventListener('touchend', this.stopDrag);

    // Live engraving sync into the "360° Setting" hero iframe (see
    // syncEngravingToHero below for the full explanation).
    window.addEventListener('message', this.onHeroEmbedMessage);
    document.addEventListener('input', this.onDocEngravingInput);
  },
  beforeUnmount() {
    window.removeEventListener('mousemove', this.onDrag);
    window.removeEventListener('mouseup', this.stopDrag);
    window.removeEventListener('touchmove', this.onDrag);
    window.removeEventListener('touchend', this.stopDrag);
    window.removeEventListener('resize', this.checkMobile);
    window.removeEventListener('resize', this.onTryonResize);
    window.removeEventListener('keydown', this._lbKeydown);
    window.removeEventListener('message', this.onHeroEmbedMessage);
    document.removeEventListener('input', this.onDocEngravingInput);
    clearTimeout(this._engravingSyncTimer);
  },
  watch: {
    changeInMetalLoading() {
      this.gallery.pos = 0;
      this.galleryImages = [...this.mediaGallery];
    },
    mediaGallery: {
      handler() {
        this.galleryImages = [...this.mediaGallery];
        this.playAllVideos();
        // A re-render can swap in a fresh iframe (new metal -> new src), which
        // announces its own 'ready'. If it's the SAME element Vue just patched,
        // it's still flagged and already live — re-push the current engraving
        // so a metal change never silently drops it.
        this.$nextTick(this.syncEngravingToHero);
      },
      deep: true,
    },
    // Re-push when the ring CHANGES under an already-live iframe — a metal
    // switch, or setting/diamond data that resolves after the embed announced
    // 'ready'. onHeroEmbedMessage fires once per iframe and onDocEngravingInput
    // only on a keystroke, so nothing else would send it.
    //
    // Not the main event on a cold load: the parent gates the hero tile on the
    // same setting.data.sku the selection needs, so the prop is normally
    // already populated by the time this component mounts and the 'ready'
    // handler sends it. Same contract as the React host either way
    // (pc-shopify Ring3DPreview: push on 'ready' AND on every change).
    frgHeroSelection() {
      this.$nextTick(this.syncEngravingToHero);
    },
  },
  methods: {
    openBannerLink(item) {
      if (item && item.link) {
        window.open(item.link, '_blank');
      }
    },
    setMainMedia(e, pos) {
      this.gallery.pos = pos;
    },
    getVideoEl(idx) {
      if (idx === 'lb') {
        var lbRef = this.$refs['lb-video'];
        return Array.isArray(lbRef) ? lbRef[0] : lbRef;
      }
      var refs = this.$refs['video-' + idx];
      return Array.isArray(refs) ? refs[0] : refs;
    },
    playAllVideos() {
      var self = this;
      this.$nextTick(function() {
        self.galleryImages.forEach(function(item, idx) {
          if (item.type !== 'video') return;
          var el = self.getVideoEl(idx);
          if (el) el.play().catch(function() {});
        });
      });
    },
    startDrag(e, idx) {
      var el = this.getVideoEl(idx);
      if (!el || !el.duration) return;
      this.drag.active = true;
      this.drag.videoIdx = idx;
      this.drag.startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
      this.drag.startTime = el.currentTime;
      el.pause();
    },
    onDrag(e) {
      if (!this.drag.active) return;
      var el = this.getVideoEl(this.drag.videoIdx);
      if (!el) return;
      var clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      var delta = clientX - this.drag.startX;
      if (Math.abs(delta) > 4) this.drag.wasDragging = true;
      var sensitivity = e.type === 'touchmove' ? 0.08 : 0.05;
      var newTime = this.drag.startTime + delta * sensitivity;
      var dur = el.duration;
      newTime = ((newTime % dur) + dur) % dur;
      el.currentTime = newTime;
      e.preventDefault();
    },
    stopDrag() {
      if (!this.drag.active) return;
      var el = this.getVideoEl(this.drag.videoIdx);
      if (el) el.play().catch(function() {});
      this.drag.active = false;
      this.drag.videoIdx = null;
      // wasDragging stays true until the click event reads and clears it
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    setMobileActive(idx) {
      this.mobileActive = idx;
      // If switching to a video, ensure it plays
      var item = this.allMobileItems[idx];
      if (item && item.srcType === 'video') {
        this.$nextTick(function() {
          var mainVid = this.$refs['mobile-main-video'];
          var el = Array.isArray(mainVid) ? mainVid[0] : mainVid;
          if (el) el.play().catch(function() {});
        }.bind(this));
      }
      // Scroll active thumb into view — horizontally only, without affecting the page scroll
      this.$nextTick(function() {
        var strip = this.$refs.thumbStrip;
        if (!strip) return;
        var thumb = strip.children[idx];
        if (!thumb) return;
        var targetLeft = thumb.offsetLeft - (strip.clientWidth / 2) + (thumb.clientWidth / 2);
        strip.scrollTo({ left: targetLeft, behavior: 'smooth' });
      }.bind(this));
    },
    scrollThumbs(direction) {
      var total = this.allMobileItems.length;
      if (!total) return;
      var next = this.mobileActive + direction;
      if (next < 0) next = total - 1;
      if (next >= total) next = 0;
      this.setMobileActive(next);
    },
    openVideoLightbox(idx) {
      if (this.drag.wasDragging) {
        this.drag.wasDragging = false;
        return;
      }
      this.openLightbox(idx);
    },
    openLightbox(idx) {
      var item = this.galleryImages[idx];
      if (!item || (item.type !== 'image' && item.type !== 'video')) return;
      this.lightbox.index = idx;
      this.lightbox.open = true;
      document.body.style.overflow = 'hidden';
    },
    closeLightbox() {
      this.lightbox.open = false;
      document.body.style.overflow = '';
    },
    lightboxNav(dir) {
      var total = this.galleryImages.length;
      var next = this.lightbox.index + dir;
      while (next >= 0 && next < total && this.galleryImages[next].type !== 'image' && this.galleryImages[next].type !== 'video') {
        next += dir;
      }
      if (next >= 0 && next < total && (this.galleryImages[next].type === 'image' || this.galleryImages[next].type === 'video')) {
        this.lightbox.index = next;
      }
    },
    _lbKeydown(e) {
      if (!this.lightbox.open) return;
      if (e.key === 'Escape') this.closeLightbox();
      if (e.key === 'ArrowLeft') this.lightboxNav(-1);
      if (e.key === 'ArrowRight') this.lightboxNav(1);
    },
    hideOnError(e) {
      var el = e.target;
      // Walk up to the .frg-cell, .frg-cell--video, or .frg-thumb wrapper and hide it
      var cell = el.closest('.frg-cell') || el.closest('.frg-cell--video') || el.closest('.frg-thumb');
      if (cell) cell.style.display = 'none';
      // For mobile main media, skip to next available item
      if (el.classList.contains('frg-mobile-main-media')) {
        el.style.display = 'none';
        // Try to advance to the next available mobile item
        if (this.allMobileItems.length > 1) {
          var next = (this.mobileActive + 1) % this.allMobileItems.length;
          this.setMobileActive(next);
        }
      }
    },
    handleGalleryImgError(e, diamondShape) {
      var target = e.target || e.currentTarget;
      var stage = parseInt(target.dataset.imgErrorStage || '0');
      target.dataset.imgErrorStage = String(stage + 1);
      if (stage === 0) {
        var fallback = getShapeFallbackImage(diamondShape);
        target.removeAttribute('srcset');
        target.src = fallback;
      } else if (stage === 1) {
        target.src = shapeToImageUrl.DEFAULT;
      } else {
        this.hideOnError(e);
      }
    },
    // ---- Try-On methods ----
    onTryonResize() {
      this.tryonContainerWidth = window.innerWidth;
    },
    setTryonHand(idx) {
      this.tryonHandIndex = idx;
    },
    setTryonCarat(ct) {
      this.tryonCarat = ct;
    },
    setTryonYours() {
      if (this.tryonYoursCarat > 0) this.tryonCarat = this.tryonYoursCarat;
    },
    // ---- Live engraving sync (hero "360° Setting" iframe) ----
    // The embed DOES support an `eng` URL param (see sealRing3dUrl) but this
    // is a live iframe already on the page, so it's synced by posting a full
    // `selection` object at it after it announces itself ready — same as the
    // product page's own live 3D viewer does while the shopper types — rather
    // than reloading it with a new `src` on every keystroke. Re-derive
    // band/shape/carat/prong/metal/gem from the hero's own `src` (already built
    // by buildRing3dEmbedUrl in handleMediaGallery) rather than threading those
    // fields down as extra props, since this component only knows the finished
    // URL, not the pieces that built it.
    // NOTE: deliberately document-scoped, NOT this.$el.querySelector. This
    // component's x-template (#final-ring-media-gallery) opens with an HTML
    // comment before its root <div>, which makes Vue 3 treat it as a
    // multi-root fragment — $el is then the leading COMMENT node, and
    // Comment.querySelector doesn't exist, so every call threw a TypeError and
    // killed the ready handler before it could arm the sync. The gallery is
    // unique on this page, so a document query is equivalent and can't break.
    heroIframeEl() {
      return document.querySelector('.frg-cell--hero .frg-cell-iframe')
        || document.querySelector('.frg-mobile-hero-iframe');
    },
    onHeroEmbedMessage(e) {
      var d = e.data;
      if (!d || d.source !== 'rmj-ring-builder') return;
      if (d.type !== 'ready' && d.type !== 'view-ready') return;
      var iframe = this.heroIframeEl();
      if (!iframe || e.source !== iframe.contentWindow) return;
      // 'view-ready' means the ring is actually on screen — the only honest
      // moment to drop the variant photo held over the stage.
      if (d.type === 'view-ready') {
        this.heroPainted = true;
        return;
      }
      // Flag the ELEMENT, not component state: handleMediaGallery() reassigns
      // pageMedia several times per load, and a component-level flag cleared by
      // that watcher after 'ready' had already fired could never be re-armed
      // (an unchanged :src doesn't reload the iframe, so no second 'ready').
      // A fresh iframe element simply starts without the flag and re-announces.
      iframe.__frgReady = true;
      this.syncEngravingToHero();
    },
    onDocEngravingInput(e) {
      var t = e.target;
      if (!t || !t.matches || !t.matches('[data-engraving-input="true"]')) return;
      clearTimeout(this._engravingSyncTimer);
      this._engravingSyncTimer = setTimeout(this.syncEngravingToHero, 120);
    },
    syncEngravingToHero() {
      var iframe = this.heroIframeEl();
      if (!iframe || !iframe.__frgReady) return;
      var base = this.frgHeroSelection;
      if (!base || !base.band) return;
      var input = document.querySelector('[data-engraving-input="true"]');
      var text = input && input.value ? input.value.trim() : '';
      var selection = {
        band: base.band,
        head: base.head,
        metal: base.metal,
        gem: base.gem,
        // An object sets the engraving; null/blank falls back to the embed's
        // own saved default — same contract as product-3d-preview.liquid.
        engraving: text ? { text: text } : null,
        variant: null,
      };
      try {
        var src = iframe.getAttribute('src') || '';
        var origin = new URL(src, window.location.href).origin;
        var win = iframe.contentWindow;
        win.postMessage({ source: 'rmj-ring-builder-host', type: 'selection', selection: selection }, origin);
        // The product page and the cart popup both follow 'ready' with this;
        // some embed builds hold rendering until told they're on screen.
        win.postMessage({ source: 'rmj-ring-builder-host', type: 'visibility', visible: true }, origin);
      } catch (err) {}
    },
  },
};

/* final ring detail table */
const FinalRingDetailTable = {
  component: {
    DiamondCaratGraphicComp,
    DiamondCutGraphicComp,
    DiamondColorGraphicComp,
    DiamondClarityGraphicComp,
  },
  data() {
    return {
      currentDetailTab: "setting",
      qrcode: new QRious({ size: 90 }),
      selectedSetting:
        JSON.parse(sessionStorage.getItem("selectedSetting")) || null,
    };
  },
  props: [
    "changeInMetalLoading",
    "settingDetails",
    "diamond",
    "selectedMetal",
    "settingId",
    "skuDetails",
  ],
  delimiters: ["[[", "]]"],
  template: `#final-ring-detail-table`,
  computed: {
    newQRCode() {
      let secondDiamond = null;
      try {
        secondDiamond = JSON.parse(sessionStorage.getItem("selectedSecondDiamond") || "null");
      } catch (e) {
        /* ignore */
      }
      let settingOptions = null;
      try {
        settingOptions = JSON.parse(sessionStorage.getItem("selectedSettingOptions") || "null");
      } catch (e) {
        /* ignore */
      }
      const ringSize = settingOptions?.settingSize;
      const routeSettingVideo = this.$route.query.settingVideo || sessionStorage.getItem("selectedSettingVideo") || this.setting?.data?.settingVideo || null;
      let matchingBandId = null;
      try {
        matchingBandId = JSON.parse(sessionStorage.getItem("selectedMatchingBand") || "null")?.id || null;
      } catch (e) {
        /* ignore */
      }
      const params = new URLSearchParams();
      try {
        const cachedSetting = JSON.parse(sessionStorage.getItem("selectedSetting") || "null");
        const cachedDiamond = JSON.parse(sessionStorage.getItem("selectedDiamond") || "null");
        if (cachedSetting && cachedDiamond) {
          const fullPayloadEncoded = encodeRingData({
            setting: {
              ...trimSettingForUrl(cachedSetting),
              settingSize: ringSize,
              settingMetal: this.$props.selectedMetal,
              settingVideo: sessionStorage.getItem("selectedSettingVideo") || cachedSetting.settingVideo || null,
            },
            diamond: cachedDiamond,
            matchingBandId: matchingBandId,
            secondDiamond: secondDiamond?.diamondId
              ? { diamondId: secondDiamond.diamondId, diamondType: secondDiamond.diamondType || "" }
              : null,
          });
          if (fullPayloadEncoded) {
            params.set("data", fullPayloadEncoded);
          }
        }
      } catch (e) {
        /* ignore */
      }
      this.qrcode.value = `${baseUrl}/collections/final-ring?${params.toString()}`;
      return this.qrcode.toDataURL();
    },
  },
  mounted() {
    this.loadSecondDiamond();
  },
  methods: {
    loadSecondDiamond() {
      const secondDiamondData = sessionStorage.getItem("selectedSecondDiamond");
      if (secondDiamondData) {
        try {
          this.secondDiamond = JSON.parse(secondDiamondData);
        } catch (e) {
          console.error("Second diamond parse error:", e);
        }
      }
    },
    openDetail(e, type) {
      let element;
      if (e.target.nodeName == "svg") {
        element = e.target.parentElement;
        e.target.parentElement.classList.toggle("active");
      } else {
        element = e.target;
        e.target.classList.toggle("active");
      }

      const panel = element.nextElementSibling;
      if (panel.style.maxHeight) {
        panel.style.maxHeight = null;
        this.currentDetailTab = "";
      } else {
        panel.style.maxHeight = panel.scrollHeight + "px";
        this.currentDetailTab = type;
      }
      element.parentElement.classList.toggle("accordion-active");
      element.parentElement.classList.toggle("accordion-non-active");
    },
  },
};

/* Setting attributes that never earn a row in "Ring Details -> Setting Details"
   (snippets/ring-builder-template-4.liquid): either they are internal plumbing,
   or the value is already on screen in the "Know your setting" / "Your Diamond
   Info" cards just above and repeating it reads as filler. Same spirit as the
   v-show filter on the old hidden final-ring-detail-table. */
const SETTING_ATTR_SKIP = [
  "Engagement_Ring_Metal_Type", // already the Metal card in "Know your setting"
  "Engagement_Ring_Carat_Weight", // the stone's own carat is in "Your Diamond Info"
  "This_Ring_Can_Be_Set_With",
  "Matching_Id",
  "Shipping_Day",
  "Setting_Config_Metal",
];

/* Row wording for those attributes. `label` overrides the humanised key and
   `tip` is the attr-tip-comp bubble -- the same info tooltip the setting detail
   page puts beside these specs (setting-detail-table in
   snippets/ring-builder-template-3.liquid). A key that isn't listed still gets
   a row, just without a tooltip, so new attributes from the API show up on
   their own. */
const SETTING_ATTR_META = {
  Engagement_Ring_Style: {
    label: "Setting Style",
    tip: "The design family of the setting -- solitaire, halo, three stone and so on. It sets how the centre diamond is framed.",
  },
  "Approx._Ring_Weight": {
    label: "Setting Weight",
    tip: "The approximate weight of the precious metal in the finished setting. It shifts slightly with your ring size and chosen metal.",
  },
  Engagement_Ring_Band_Width: {
    label: "Band Width",
    tip: "The width of the band measured at the base of the ring, expressed in millimetres.",
  },
  Engagement_Ring_Prong_Type: {
    label: "Prong Type",
    tip: "The prong style that holds your diamond. Round prongs are classic; claw prongs maximise light entry.",
  },
};

/* final page metat data */
const FinalRingMetaData = {
  components: {
    TooltipComp,
    SocialIconComp,
    DiamondEmailPopupComp,
    diamondConfirmationPopup,
    DiamondHintPopupComp,
    waitLoader,
    StickyMobileBarComp,
    AttrTipComp,
    DiamondSoldOutPopup,
    DiamondPriceChangePopup,
  },
  props: [
    "redirectionPossible",
    "changeInMetalLoading",
    "setting",
    "diamond",
    "settingSize",
    "settingMetal",
    "settingSizeList",
    "settingCountry",
    "finalSettingSize",
    "settingMetalList",
    "matchingBandAvailable",
    "isPrimary",
  ],
  emits: [
    "update:setting-size",
    "update:setting-country",
    "update:setting-metal",
    "resetSettingSize",
    "setFinalSize",
    "updateMetal",
    "scrollToMatchingBand",
  ],
  data() {
    return {
      isOpen: false,
      loading: false,
      successMessage: "",
      isSubmitting: false,
      errorMessage: "",
      form: {
        EMAIL: "",
        FNAME: "",
        LNAME: "",
        PHONE: "",
        MMERGE6: window.location.href, // pre-fill with current URL
      },
      buttonText: "Complete Purchase",
      buttonClicked: false,
      showBtnLoader: false,
      saveButtonText: "Save For Later",
      saveButtonClicked: false,
      saveShowBtnLoader: false,
      shipsByDate: "",
      showRingSizes: false,
      showMetalChange: false,
      showTooltip: false,
      tooltipText: "",
      tooltipClass: "",
      getDiamondShippingDays: "",
      coupon_ring_img: `${baseAssetsUrl}/coupon_studs.png`,
      isDDOpen: false,
      showShareLinkCopied: false,
      showSocialIconDiv: false,
      openHintPopup: false,
      openEmailPopup: false,
      openRequestDiamondImgPopup: false,
      openConfPopup: false,
      waitLoader: false,
      loaderMessage: "",
      replaceDiamond: "",
      replaceSetting: "",
      engravingText: "",
      showEngravingInput: false,
      showEngravingInfo: false,
      selectedSecondDiamond: "",
      selectedDiamond: "",
      matchingBand: "",
      selectedSetting:
        JSON.parse(sessionStorage.getItem("selectedSetting")) || {},
      showCartConflictModal: false,
      cartConflictItems: [],
      showSoldOutPopup: false,
      soldOutDiamond: null,
      showPriceChangePopup: false,
      priceChangedDiamond: null,
      showDropHint: false,
      dahSent: false,
      dahSubmitting: false,
      dahSenderName: "",
      dahRecipientName: "",
      dahRecipientEmail: "",
      dahErrors: {},
      dahCopied: false,
_pendingAddToCartEvent: null,
      _skipConflictCheck: false,
      piRingOpen: false,
      piStoneOpen: false,
      piSettingOpen: false,
      piShipOpen: false,
      piReturnOpen: false,
      /* sessionStorage only -- the opt-in dies with the tab
         (snippets/free-gift.liquid). */
      rbFinalGiftToggle: sessionStorage.getItem('apply_free_gift') === 'true',
    };
  },
  watch: {
    rbFinalGiftToggle(newVal) {
      var strVal = newVal ? 'true' : 'false';
      if (sessionStorage.getItem('apply_free_gift') !== strVal) {
        sessionStorage.setItem('apply_free_gift', strVal);
        window.dispatchEvent(new CustomEvent('freeGiftToggleChanged', { detail: { checked: newVal } }));
        /* Act on the real cart straight away, the way the PDP and the drawer
           do -- a cart that already clears the threshold gets the gift the
           moment the box is ticked, and loses it the moment it is unticked,
           instead of waiting for the shopper to reach /cart.
           (snippets/free-gift.liquid) */
        if (typeof window.ensureFreeGiftInCart === 'function') window.ensureFreeGiftInCart();
      }
    },
    engravingText: {
      immediate: true,
      handler(val) {
        this.engravingText = this.filterEngravingText(val);
      },
    },
    // The diamond prop is `{}` until the parent (FinalRingDetailPageComp) finishes its own
    // mounted() — which, per Vue's child-before-parent mount order, happens AFTER this
    // component's own mounted() has already fired, so calling checkDisplayedDiamondPriceOnLoad
    // directly from mounted() would always see an empty diamond and silently no-op. Watching
    // the prop instead fires the check exactly once, whenever diamondId actually first
    // becomes populated, regardless of whether that happens before or after this component
    // mounts (immediate: true covers the "already populated by mount time" case too).
    "diamond.diamondId": {
      immediate: true,
      handler(diamondId) {
        if (!diamondId) return;
        this.checkDisplayedDiamondPriceOnLoad(this.$props.diamond, "testDiamondPrice");
      },
    },
    // Mirrors the "diamond.diamondId" watcher above, for the second (toi-et-moi) stone.
    // selectedSecondDiamond is plain component data (not a prop) populated either at this
    // component's own mounted() (already cached in sessionStorage) or later, once the
    // page-level loadSecondDiamondFromUrl() refetch finishes and calls this component's
    // loadSecondDiamond() — immediate: true plus watching the nested id covers both timings
    // the same way the primary diamond's watcher does.
    "selectedSecondDiamond.diamondId": {
      immediate: true,
      handler(diamondId) {
        if (!diamondId) return;
        this.checkDisplayedDiamondPriceOnLoad(this.selectedSecondDiamond, "testSecondDiamondPrice");
      },
    },
    redirectionPossible() {
      this.waitLoader = this.$props.redirectionPossible;
      let page = "Diamond";
      let redirection = sessionStorage.getItem("redirection");
      this.loaderMessage = `You are redirecting to ${redirection == "engagement-rings" ? "Setting" : page
        } page`;
    },
  },
  delimiters: ["[[", "]]"],
  template: `#final-ring-meta-data`,
  mounted() {
    // Pre-fill from whatever was typed on the product page, same priority order
    // buildSettingFromVariant() uses for prongType: the carried-forward URL query
    // param first (freshest — set by ring-builder.js's finalRingPage beforeEnter
    // guard from the ?data= payload), then this component's own `setting` prop
    // (setting.data.engravingText, populated the same way on a cache-hit load).
    // The watch{} above still re-filters whatever lands here, so this is safe to
    // assign directly.
    var carriedEngraving = this.$route.query.engravingText || (this.$props.setting && this.$props.setting.engravingText) || "";
    if (carriedEngraving) this.engravingText = this.filterEngravingText(carriedEngraving);
    this.loadMatchingBand();
    if (sessionStorage.getItem("selectedSecondDiamond")) {
      this.selectedSecondDiamond = JSON.parse(
        sessionStorage.getItem("selectedSecondDiamond")
      );
    }
    this.getShipsByDate();
    document.addEventListener("click", () => (this.isDDOpen = false));
    this.checkCartDiamondPriceOnLoad();
    window.addEventListener("freeGiftToggleChanged", (e) => {
      if (this.rbFinalGiftToggle !== e.detail.checked) {
        this.rbFinalGiftToggle = e.detail.checked;
      }
    });
    fetch("/cart.js")
      .then((r) => r.json())
      .then((cart) => {
        var hasGift = cart.items && cart.items.some((i) => i.variant_id === 67834200031596 || (i.properties && i.properties._gift));
        if (hasGift) {
          this.rbFinalGiftToggle = true;
          sessionStorage.setItem("apply_free_gift", "true");
        }
      })
      .catch(() => {});
  },
  computed: {
    getPageTitle() {
      const settingName = this.$props.setting?.name?.split("/")?.shift();
      const firstDiamond = getDiamondTitle(this.$props.diamond, this.$route.query.diamondType);
      let title = `${settingName} With ${firstDiamond}`;
      if (this.selectedSecondDiamond && this.selectedSecondDiamond.shape) {
        title += ` & ${getDiamondTitle(this.selectedSecondDiamond, this.selectedSecondDiamond.diamondType)}`;
      }
      if (this.matchingBand && this.matchingBand.title) {
        const bandName = String(this.matchingBand.title).split(' - ').shift();
        title += ` & Matching ${bandName}`;
      }
      return title;
    },
    getPopupTitle() {
      return `${this.$props.setting?.name
        ?.split("/")
        ?.shift()} With ${getDiamondTitle(this.$props.diamond)} ${this.$route.query.diamondType == "white"
          ? "Natural"
          : this.$route.query.diamondType == "white_color"
            ? "Coloured Natural"
            : this.$route.query.diamondType == "lab_color"
              ? "Coloured Lab Grown"
              : "Lab Grown"
        } Diamond`;
    },
    getPopupImage() {
      return this.$props.diamond.main_image;
    },
    getPopupPlaceholderImage() {
      return this.$props.diamond.main_image;
    },
    getConfirmPopupTitle() {
      return (
        '"' +
        `${`${this.$props.setting.name} With ${getDiamondTitle(
          this.$props.diamond
        )} ${this.$route.query.diamondType == "white" ? "Natural" : "Lab Grown"
        } Diamond`}` +
        '"'
      );
    },
    getContactPhoneNumber() {
      return contactTelephoneNumber;
    },
    getTelephoneLink() {
      return `tel:${contactTelephoneNumber.replace(/[^0-9]/g, "")}`;
    },
    /* Setting spec rows for "Ring Details -> Setting Details", each with the
       same info tooltip the setting detail page shows beside these specs.
       Prefer the `setting` prop over the sessionStorage copy: the prop tracks
       a metal change made on this page, `selectedSetting` is read once in
       data() and would go stale. */
    settingAttrRows() {
      const attrs =
        this.$props.setting?.attributes || this.selectedSetting?.attributes || {};
      /* The raw API attributes are key -> string, but the setting detail page
         wraps the same specs as { value, description }. Unwrap either shape so
         a wrapped value never renders as "[object Object]". */
      const plain = (val) =>
        val && typeof val === "object" ? val.value : val;
      return Object.keys(attrs)
        .filter((key) => {
          if (!key || SETTING_ATTR_SKIP.indexOf(key) !== -1) return false;
          if (/video/i.test(key)) return false;
          const val = plain(attrs[key]);
          return val !== null && val !== undefined && String(val).trim() !== "";
        })
        .map((key) => {
          const meta = SETTING_ATTR_META[key] || {};
          return {
            key: key,
            label:
              meta.label ||
              key.split("_").join(" ").replace("Engagement Ring", "").trim(),
            tip: meta.tip || "",
            value: String(plain(attrs[key])),
          };
        });
    },
  },
  methods: {
    getDiamondCardTitle(diamond) {
      if (!diamond) return "";
      return getDiamondTitle(diamond, diamond.diamondType || this.$route.query.diamondType);
    },
    applyLabDiscount(price, type) {
      if (!price) return 0;
      var numericPrice = Math.round(price);
      if ((type === 'lab' || type === 'lab_color') && numericPrice >= 500) {
        return Math.round(numericPrice * 0.70);
      }
      return numericPrice;
    },
    isLabDiscounted(price, type) {
      if (!price) return false;
      return (type === 'lab' || type === 'lab_color') && Math.round(price) >= 500;
    },
    finalTotalPrice() {
      //  main diamond
      const mainDiamond = this.diamond
        ? this.applyLabDiscount(this.diamond.finalPriceAud, this.diamond.diamondType)
        : 0;

      //  second diamond
      const secondDiamond = this.selectedSecondDiamond
        ? this.applyLabDiscount(
          this.selectedSecondDiamond.finalPrice,
          this.selectedSecondDiamond.diamondType
        )
        : 0;


      //  setting price (no discount)
      const settingPrice = Math.round(
        this.adjustPriceByRingSize(this.settingSize, this.setting.price) || 0
      );


      //  matching band (10% off always)
      const bandPrice = this.matchingBand
        ? Math.round(
          parseFloat(this.matchingBand.price.replace(/,/g, '')) * 0.90
        )
        : 0;
      const total = mainDiamond + secondDiamond + settingPrice + bandPrice;
      return total;
    },
    totalSavings() {
      var saved = 0;
      // diamond discount
      if (this.diamond && this.diamond.finalPriceAud && this.isLabDiscounted(this.diamond.finalPriceAud, this.diamond.diamondType)) {
        saved += Math.round(this.diamond.finalPriceAud) - this.applyLabDiscount(this.diamond.finalPriceAud, this.diamond.diamondType);
      }
      // second diamond discount
      if (this.selectedSecondDiamond && this.selectedSecondDiamond.finalPrice && this.isLabDiscounted(this.selectedSecondDiamond.finalPrice, this.selectedSecondDiamond.diamondType)) {
        saved += Math.round(this.selectedSecondDiamond.finalPrice) - this.applyLabDiscount(this.selectedSecondDiamond.finalPrice, this.selectedSecondDiamond.diamondType);
      }
      // matching band discount (10%)
      if (this.matchingBand && this.matchingBand.price) {
        saved += Math.round(parseFloat(this.matchingBand.price.replace(/,/g, '')) * 0.10);
      }
      return saved;
    },
    loadMatchingBand() {
      var bandData = sessionStorage.getItem("selectedMatchingBand");
      if (bandData) {
        var parsed = JSON.parse(bandData);
        //  Ensure price is always a string so .replace(/,/g,'') works in template
        parsed.price = String(parsed.price);
        this.matchingBand = parsed;
      } else {
        this.matchingBand = null;
      }
    },
    loadSecondDiamond() {
      var secondDiamondData = sessionStorage.getItem("selectedSecondDiamond");
      if (secondDiamondData) {
        try {
          this.selectedSecondDiamond = JSON.parse(secondDiamondData);
        } catch (e) {
          /* ignore */
        }
      }
    },

    removeMatchingBand() {
      sessionStorage.removeItem("selectedMatchingBand");
      this.matchingBand = null;
      if (this.$parent && this.$parent.$router && this.$parent.$route) {
        const query = { ...this.$parent.$route.query };
        delete query.matchingBandId;
        this.$parent.$router.replace({ query });
      }
      if (this.$parent && this.$parent.initMatchingBandSection) {
        this.$parent.initMatchingBandSection();
      }
      if (this.$parent && this.$parent.handleStickyBarItems) {
        this.$parent.handleStickyBarItems();
      }
    },
    getDiamondDetailLink(diamond) {
      return `/collections/${diamond.diamondType == "lab"
        ? "lab-diamonds"
        : diamond.diamondType == "lab_color"
          ? "coloured-lab-grown-diamonds"
          : diamond.diamondType == "white"
            ? "natural-diamonds"
            : diamond.diamondType == "white_color"
              ? "natural-fancy-color-diamond"
              : "/"
        }/diamonds?diamondId=${diamond.diamondId}`;
    },
    getSecondDiamondTitle() {
      return `${getDiamondTitle(this.selectedSecondDiamond)} ${this.selectedSecondDiamond.diamondType == "lab"
        ? "Lab Grown Diamond"
        : this.selectedSecondDiamond.diamondType == "lab_color"
          ? "Coloured Lab Grown Diamond"
          : this.selectedSecondDiamond.diamondType == "white"
            ? "Natural Diamond"
            : this.selectedSecondDiamond.diamondType == "white_color"
              ? "Coloured Natural Diamond"
              : this.selectedSecondDiamond.diamondType
        }`;
    },
    getFirstDiamondTitle(diamond) {
      return `${getDiamondTitle(diamond)} ${diamond.diamondType == "lab"
        ? "Lab Grown Diamond"
        : diamond.diamondType == "lab_color"
          ? "Coloured Lab Grown Diamond"
          : diamond.diamondType == "white"
            ? "Natural Diamond"
            : diamond.diamondType == "white_color"
              ? "Coloured Natural Diamond"
              : diamond.diamondType
        }`;
    },
    openForm() {
      this.isOpen = true;
    },
    closeForm() {
      this.isOpen = false;
    },
    closeOnOverlay(e) {
      if (e.target.classList.contains("cad-free-cad-popup-overlay")) {
        this.closeForm();
      }
    },
    async submitMailchimp() {
      this.errorMessage = "";
      this.successMessage = "";
      this.isSubmitting = true;

      try {
        const form = document.getElementById("mc-embedded-subscribe-form");
        const formData = new FormData(form);

        // Mailchimp expects URL-encoded data
        const queryString = new URLSearchParams(formData).toString();

        const url =
          "https://ringsofaustralia.us17.list-manage.com/subscribe/post-json?u=bd5edca466a37b5cb7a5021e2&id=feaed77a7d&c=?";

        // JSONP request for Mailchimp
        const response = await fetch(`${url}&${queryString}`, {
          method: "GET",
          mode: "no-cors",
        });

        // Mailchimp returns a JSONP callback - we cannot parse it directly due to no-cors
        // So, we just assume success if no network error
        this.successMessage =
          " Your request was submitted successfully! We'll contact you soon.";
        this.closeForm();
        this.showTooltip = true;
        this.tooltipText =
          response?.data?.msg ||
          " Your request was submitted successfully! We'll contact you soon.";
        this.tooltipClass = "";
        setTimeout(() => {
          this.showTooltip = false;
        }, 2000);
      } catch (error) {
        console.error("Mailchimp submission error:", error);
        this.errorMessage =
          "❌ There was a problem submitting your request. Please try again.";
        this.showTooltip = true;
        this.tooltipText =
          "❌ There was a problem submitting your request. Please try again!";
        this.tooltipClass = "";
        setTimeout(() => {
          this.showTooltip = false;
        }, 2000);
      } finally {
        this.isSubmitting = false;
      }
    },

    removeFirstDiamondFromSession() {
      // Get diamond from session
      let diamond = JSON.parse(
        sessionStorage.getItem("selectedDiamond") || "{}"
      );
      if (!diamond || !diamond.diamondId) return;

      sessionStorage.removeItem("selectedDiamond");
      sessionStorage.removeItem("searchingSecondDiamond");

      const collectionPath =
        diamond.diamondType === "white"
          ? "natural-diamonds"
          : diamond.diamondType === "lab"
            ? "lab-diamonds"
            : diamond.diamondType === "white_color"
              ? "natural-fancy-coloured-diamonds"
              : diamond.diamondType === "lab_color"
                ? "coloured-lab-grown-diamonds"
                : "";
      window.location.href = `/${collectionPath}`;
      if (this.$refs.mainAnchor)
        this.$refs.mainAnchor.href = `/${collectionPath}`;
    },
    removeMatchingBandFromSession() {
      // Get the current selectedSetting from sessionStorage
      let selectedSetting = JSON.parse(
        sessionStorage.getItem("selectedSetting") || "{}"
      );
      if (!selectedSetting || !selectedSetting.matching_band) return;

      delete selectedSetting.matching_band;

      // Update sessionStorage
      sessionStorage.setItem(
        "selectedSetting",
        JSON.stringify(selectedSetting)
      );

      this.selectedSetting = selectedSetting;

      // console.log("  matching_band removed from sessionStorage and Vue state");
    },

    replaceSecondDiamond() {
      sessionStorage.setItem("searchingSecondDiamond", true);

      // Redirect to diamonds page (keep existing second diamond in session until a new one is chosen)
      window.location.href = `${baseUrl}/collections/${this.$props.diamond.diamondType == "lab"
        ? "lab"
        : this.$props.diamond.diamondType == "lab_color"
          ? "coloured-lab-grown"
          : this.$props.diamond.diamondType == "white"
            ? "natural"
            : this.$props.diamond.diamondType == "white_color"
              ? "natural-color"
              : ""
        }-diamonds`;
    },

    selectSecondDiamond() {
      sessionStorage.setItem("searchingSecondDiamond", true);
      let type = this.$props.diamond.diamondType;
      if (type === "white") {
        window.location.href = `${baseUrl}/collections/natural-diamonds`;
      } else if (type === "white_color") {
        window.location.href = `${baseUrl}/collections/natural-fancy-coloured-diamonds`;
      } else if (type === "lab") {
        window.location.href = `${baseUrl}/collections/lab-diamonds`;
      } else if (type === "lab_color") {
        window.location.href = `${baseUrl}/collections/coloured-lab-grown-diamonds`;
      } else {
        console.warn("⚠️ Unknown diamond type:", type);
      }
    },

    filterEngravingText(text) {
      // Strip characters the engraving font can't render, keep spaces (an
      // engraving is usually more than one word), cap at 10 characters.
      return text.replace(/[^a-zA-Z0-9 ]/g, "").slice(0, 10);
    },
    getDetailPage() {
      if (sessionStorage.getItem("selectedSetting") != null) {
        const selectedSetting = JSON.parse(
          sessionStorage.getItem("selectedSetting")
        );
        if (selectedSetting.current_url != null) {
          window.location.href = selectedSetting.current_url;
        } else {
          window.location.href = `/collections/engagement-rings`;
        }
      }
    },
    adjustPriceByRingSize(ringSize, price) {
      return price;

      // Define ring sizes in order and the base size for comparison
      const ringSizes = [
        "Select Ring Size",
        "E",
        "E 1/2",
        "F",
        "F 1/2",
        "G",
        "G 1/2",
        "H",
        "H 1/2",
        "I",
        "I 1/2",
        "J",
        "J 1/2",
        "K",
        "K 1/2",
        "L",
        "L 1/2",
        "M",
        "M 1/2",
        "N",
        "N 1/2",
        "O",
        "O 1/2",
        "P",
        "P 1/2",
        "Q",
        "Q 1/2",
        "R",
        "R 1/2",
        "S",
        "S 1/2",
        "T",
        "T 1/2",
        "U",
        "U 1/2",
        "V",
        "V 1/2",
        "W",
        "W 1/2",
        "X",
        "X 1/2",
        "Y",
        "Y 1/2",
        "Z",
        "Z 1/2",
      ];
      const baseSize = "N"; // Base size threshold for price adjustment

      // Check if price is a string and clean up formatting if necessary
      let numericPrice =
        typeof price === "string"
          ? parseFloat(price.replace(/[^0-9.]/g, ""))
          : price;

      // Ensure the price is a valid number
      if (isNaN(numericPrice)) {
        // console.error("Invalid price input");
        return null; // Handle invalid price as needed
      }

      // Find indexes for the selected ring size and base size
      const ringSizeIndex = ringSizes.indexOf(ringSize);
      const baseSizeIndex = ringSizes.indexOf(baseSize);

      // Adjust the price if the ring size index is greater than or equal to the base size index
      return ringSizeIndex >= baseSizeIndex ? numericPrice + 20 : numericPrice;
    },
    adjustPriceByRingSizeForPayload(ringSize) {
      // Define ring sizes in order and the base size for comparison
      const ringSizes = [
        "Select Ring Size",
        "E",
        "E 1/2",
        "F",
        "F 1/2",
        "G",
        "G 1/2",
        "H",
        "H 1/2",
        "I",
        "I 1/2",
        "J",
        "J 1/2",
        "K",
        "K 1/2",
        "L",
        "L 1/2",
        "M",
        "M 1/2",
        "N",
        "N 1/2",
        "O",
        "O 1/2",
        "P",
        "P 1/2",
        "Q",
        "Q 1/2",
        "R",
        "R 1/2",
        "S",
        "S 1/2",
        "T",
        "T 1/2",
        "U",
        "U 1/2",
        "V",
        "V 1/2",
        "W",
        "W 1/2",
        "X",
        "X 1/2",
        "Y",
        "Y 1/2",
        "Z",
        "Z 1/2",
      ];
      const baseSize = "N"; // Base size threshold for price adjustment

      // Find indexes for the selected ring size and base size
      const ringSizeIndex = ringSizes.indexOf(ringSize);
      const baseSizeIndex = ringSizes.indexOf(baseSize);

      // Adjust the price if the ring size index is greater than or equal to the base size index
      // return ringSizeIndex >= baseSizeIndex ? 20 : 0;
      return 0;
    },
    updateWaitLoader(bool = undefined, message = undefined) {
      this.waitLoader = !!bool;
      this.loaderMessage = message || "";
    },
    async copyCoupon(code) {
      const el = document.createElement("textarea");
      el.value = code;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      this.showTooltip = true;
      this.tooltipText = "Coupon Code Copied!";
      this.tooltipClass = "";
      setTimeout(() => {
        this.showTooltip = false;
      }, 2000);

      // setTimeout(()=>{
      //     this.handleBtnTooltip('hide','copyCoupons')
      // },2000)
    },
    handleBtnTooltip() {
      this.showTooltip = true;
      this.tooltipText = "Link Copied";
      this.tooltipClass = "";
      const timer = setTimeout(() => {
        // clearTimeout(timer);
        this.showTooltip = false;
      }, 2000);
    },
    openHintPopupForm() {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(["openForm", "Y9rtKH"]);
    },
    openFormPopup(e, formType) {
      if (formType === "email_us") {
        this.openEmailPopup = true;
      } else if (formType === "request_diamond_img") {
        this.openRequestDiamondImgPopup = true;
      } else if (formType === "drop_a_hint") {
        this.openHintPopup = true;
      }
    },
    showSocialIconSection() {
      if (this.showSocialIconDiv) {
        clearTimeout(this.iconTimeout); // Clear any existing timeout
        this.showSocialIconDiv = false;
      } else {
        this.showSocialIconDiv = true;
        this.iconTimeout = setTimeout(() => {
          this.showSocialIconDiv = false;
        }, 5000);
      }
    },
    openDropDown(event) {
      event.stopPropagation();
      this.isDDOpen = true;
    },
    redirectToAppointmentPage() {
      location.href = `${baseUrl}/book-an-appointment`;
    },
    getShipsByDate() {
      var week = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      var month = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      let date = new Date(
        new Date(new Date()).setDate(new Date(new Date()).getDate() + 35)
      );
      this.getDiamondShippingDays = `${week[date.getDay()]}, ${month[date.getMonth()]
        } ${date.getDate()}`;
    },
    closeConfirmationPopup() {
      this.openConfPopup = false;
    },

    diamondPayloadTitle(diamond) {
      // 0.3 Ct. D Color VVS2 Clarity Round Shape Lab Grown Diamond
      const { color, clarity, caratWeight, shape, diamondType } = diamond;
      const diamondT =
        diamondType == "lab"
          ? "Lab Grown"
          : diamondType == "lab_color"
            ? "Coloured Lab Grown"
            : diamondType == "white_color"
              ? "Coloured Natural"
              : "Natural";
      return `${caratWeight} ct ${color} Colour ${clarity} Clarity ${shape} Shape ${diamondT} Diamond`;
    },

    /* The one place this page reads /cart.js. window.ROACart
       (snippets/cart-fetch-dedupe.liquid) shares a single network request
       between every reader on the page, so calling this twice in a row costs
       one request. */
    async readCart() {
      if (window.ROACart && typeof window.ROACart.get === "function") {
        return window.ROACart.get();
      }
      const response = await fetch(window.Shopify.routes.root + "cart.js", {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    },

    /* `cart`: pass the cart addToCart() already read so this does not fetch it
       again; without one it reads it itself. */
    async checkCartData(type, id, cart) {
      try {
        const cartData = cart || (await this.readCart());

        // Find the matching product in the cart
        const existingProduct = cartData.items.find(
          (item) => item.product_type === type && item.sku === id
        );

        return existingProduct || null; // Return the product data or null if not found
      } catch (error) {
        console.error("Failed to fetch cart data:", error);
        return false; // Default to false in case of an error
      }
    },

    diamondTypeToProductType(diamondType) {
      return (
        {
          lab: "Lab Diamonds",
          lab_color: "Lab Colour Diamonds",
          white: "Natural Diamonds",
          white_color: "Natural Colour Diamonds",
        }[diamondType] || diamondType
      );
    },
    async createCustomProductWithMetafields(diamondData) {
      const productCreateApiEndpoint =
        window.ShopifyConfig.productCreateApiEndpoint; // Replace with your Admin API access token

      const bodyData = {
        product: {
          title: this.diamondPayloadTitle(diamondData),
          vendor: "Rings Of Australia",
          product_type: this.diamondTypeToProductType(diamondData.diamondType),
          status: "active",
          tags: `Diamonds,hidden,${diamondData.shape || "Shape"},${diamondData.caratWeight
            }-carat`,
          images: [{ src: diamondData.diamondImage }],
          variants: [
            {
              option1: `Diamond Certificate: ${diamondData.diamondId}`,
              price: diamondData.finalPriceAud,
              sku: diamondData.diamondId,
            },
          ],
        },
      };

      try {
        const response = await fetch(productCreateApiEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
            // "X-Shopify-Access-Token": accessToken,
          },
          body: JSON.stringify(bodyData),
        });

        let responseData = await response.json();
        responseData = responseData;

        if (responseData.product) {
          return responseData.product;
        } else {
          console.error("Failed to create product:", responseData.errors);
          throw new Error("Somthing went wrong! Please try again.");
          return null;
        }
      } catch (error) {
        console.error("Error while creating product:", error);
        throw new Error("Somthing went wrong! Please try again.");
        return null;
      }
    },
    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    /* Thrown when the product the builder just created never shows up on the
       storefront (checkStorefrontWithRetry() returned false). Adding it would
       only bounce, so addToCart() stops here with this message instead. */
    storefrontNotReadyError() {
      const err = new Error("Storefront product not available yet");
      err.friendlyMessage =
        "We couldn't finish preparing your diamond. Please try again in a moment.";
      return err;
    },

    /* Polls /products/<handle>.js until the product the builder just created is
       available on the storefront. Resolves true when it is, false when it never
       becomes so within the backoff schedule — the caller must stop on false.
         - Same URL every time: the old ?cacheBust=Date.now() forced Shopify's CDN
           to miss on every poll, which is origin load the store cannot spare.
           cache: 'no-store' keeps the browser from replaying a 404 it saw a
           moment earlier without touching the CDN.
         - Backoff (0.8s, 1.5s, 3s, 5s) instead of a flat 2s: a product that
           needs longer gets it without five requests in ten seconds. */
    async checkStorefrontWithRetry(handle, waits = [800, 1500, 3000, 5000]) {
      for (let attempt = 0; ; attempt++) {
        try {
          const res = await fetch(`${window.location.origin}/products/${handle}.js`, {
            cache: "no-store",
          });
          if (res.ok) {
            const data = await res.json();
            if (data?.available) return true;
          }
        } catch (err) {
          /* not there yet — fall through to the wait */
        }
        if (attempt >= waits.length) return false;
        await this.delay(waits[attempt]);
      }
    },

    /* Compares each given stone with its current price in the diamond detail API and
       returns a changedEntry for the first one that differs — or null if none have /
       window.ROADiamond isn't ready.
         - A stone already in the cart is checked by its cart LINE's price
           (ROADiamond.checkCartLinePrice): that's what would actually be charged, and
           addToCart() re-uses that line's variant rather than creating a new product.
         - With includeDisplayed, a stone NOT in the cart is checked by the price this page
           holds for it (finalPriceAud from sessionStorage / ?data=), which is the price
           addToCart() would create its Shopify product at.
       Cart-line entries carry variantId/lineKey, so closePriceChangePopup() reprices that
       line; displayed-price entries don't. Shared by the passive on-load check (mounted())
       and the addToCart() pre-flight check. */
    async findChangedDiamondPrice(stones, includeDisplayed = false, knownCart = null) {
      const RD = window.ROADiamond;
      if (!RD?.checkCartLinePrice) return null;
      // addToCart() hands in the cart it already read; the on-load check reads its own.
      const cart = knownCart || (await this.readCart());
      for (const stone of stones) {
        const existingLine = cart.items.find(
          (item) => RD.isDiamondLineItem(item) && item.sku === stone.diamondId
        );
        if (existingLine) {
          // The whole cart, not just its currency: checkCartLinePrice reads
          // original_total_price to work out the discount the line is displayed at.
          const lineResult = await RD.checkCartLinePrice(existingLine, cart);
          if (lineResult.status === "changed") return lineResult.changed;
          continue;
        }
        if (!includeDisplayed) continue;

        const result = await RD.fetchDiamondDetails({
          diamondId: stone.diamondId,
          diamondType: stone.diamondType,
        });
        if (result.status !== "available") continue;
        const displayedCents = Math.round(parseFloat(stone.finalPriceAud) * 100);
        const liveCents = Math.round(parseFloat(result.diamond.finalPriceAud) * 100);
        if (!Number.isFinite(displayedCents) || !Number.isFinite(liveCents)) continue;
        if (Math.abs(liveCents - displayedCents) > 1) {
          return {
            label: RD.describeDiamond(stone),
            diamondType: stone.diamondType,
            diamondId: stone.diamondId,
            previousPrice: displayedCents,
            updatedPrice: liveCents,
          };
        }
      }
      return null;
    },

    /* Checks whatever diamond price the page loaded with (from sessionStorage — either a
       normal selection, or seeded by a shared ?data= URL, or hand-edited directly in
       DevTools for QA) against that diamond's current price per the diamond detail API.
       Surfaces the "Diamond Price Has Changed" popup on a mismatch, same as
       checkCartDiamondPriceOnLoad's cart-based check, but independent of whether the
       diamond is in the cart at all — this only needs the diamond currently shown on the
       page itself.

       QA hook: ?testDiamondPrice=<dollars> (primary diamond) / ?testSecondDiamondPrice=
       <dollars> (toi-et-moi second stone) in the URL stands in for a stale cached price:
       the page shows that static price straight away (in memory only — sessionStorage keeps
       the real one), and it's the "Previous Price" side of the comparison. It does NOT
       replace the live API call, which always runs and is always trusted as the "Updated
       Price" / ground truth — so on a mismatch the popup opens, and closing it swaps the
       page over to the live price (closePriceChangePopup). This lets a tester watch the
       whole flow without waiting on the real feed to move or hand-editing sessionStorage.
       Pass testPriceQueryKey = null to skip the hook (the post-close re-check does).

       Guards against clobbering an already-surfaced popup: the primary and second diamond
       are checked independently (see the two mirrored watchers above) and can resolve in
       either order, but only one price-changed diamond can be shown at a time — the other
       is picked up by the re-check that runs after the popup closes. */
    async checkDisplayedDiamondPriceOnLoad(displayedDiamond, testPriceQueryKey) {
      if (!displayedDiamond?.diamondId) return;
      const testPrice = testPriceQueryKey ? parseFloat(this.$route.query[testPriceQueryKey]) : NaN;
      if (Number.isFinite(testPrice) && testPrice > 0) {
        this.setDisplayedDiamondPrice(displayedDiamond, testPrice);
      }
      if (!displayedDiamond.finalPriceAud) return;
      if (this.showPriceChangePopup) return;
      try {
        const response = await axios({
          url: api,
          method: "post",
          data: {
            query: `{
                ${diamondDetailApiFunc}(diamondId:"${displayedDiamond.diamondId}",diamondType:"${displayedDiamond.diamondType || ""}") {
                    diamond{
                            diamondId
                            finalPriceAud
                            availability
                        }
                }
            }`,
          },
        });
        const node = response?.data?.data?.[diamondDetailApiFunc];
        const live = (node && node.diamond && node.diamond[0]) || null;
        if (!live || !live.finalPriceAud) return;
        if (/sold|out of stock|unavailable/i.test(String(live.availability || ""))) return;
        if (this.showPriceChangePopup) return; // re-check: the other stone's check may have won the race while this axios call was in flight

        const displayedCents = Math.round(parseFloat(displayedDiamond.finalPriceAud) * 100);
        const liveCents = Math.round(parseFloat(live.finalPriceAud) * 100);
        if (!Number.isFinite(displayedCents) || !Number.isFinite(liveCents)) return;
        if (Math.abs(liveCents - displayedCents) > 1) {
          this.priceChangedDiamond = {
            label: window.ROADiamond?.describeDiamond?.(displayedDiamond) || "The selected diamond",
            diamondType: displayedDiamond.diamondType,
            diamondId: displayedDiamond.diamondId,
            previousPrice: displayedCents,
            updatedPrice: liveCents,
          };
          this.showPriceChangePopup = true;
        }
      } catch (error) {
        console.error("Failed to check displayed diamond price on load:", error);
      }
    },

    /* Passive check, run once when the final-ring page loads: if the diamond(s) already
       shown on this page are also sitting in the cart at a stale price, surface the
       "Diamond Price Has Changed" popup immediately — the shopper doesn't have to click
       "Complete Purchase" first to find out. */
    async checkCartDiamondPriceOnLoad() {
      const stones = [this.$props.diamond].filter((d) => d?.diamondId);
      if (
        this.$props.setting?.tags?.includes("toi-et-moi") &&
        this.selectedSecondDiamond?.diamondId
      ) {
        stones.push(this.selectedSecondDiamond);
      }
      if (!stones.length) return;
      try {
        const changedEntry = await this.findChangedDiamondPrice(stones);
        if (changedEntry) {
          this.priceChangedDiamond = changedEntry;
          this.showPriceChangePopup = true;
        }
      } catch (error) {
        console.error("Failed to check cart diamond price on load:", error);
      }
    },

    /* Close the price-change popup and bring the price in line with the diamond detail API.

       A cart-line entry (it has a variantId) is repriced on the backend through
       ROADiamond.repriceCartLine (assets/diamond-availability.js): a new Shopify product is
       created at the API price, added with the same line properties, and only then is the
       old stale-priced line removed — the diamond is never out of the cart, and a failure
       leaves the original line untouched (the addToCart() pre-flight check then catches it
       again). The page loader stays up meanwhile, and addToCart() waits on
       diamondPriceSync, so "Complete Purchase" can't race the swap.

       Not every entry has a variantId: checkDisplayedDiamondPriceOnLoad (and the pre-flight
       check) raise this same popup for a diamond that isn't in the cart yet, so there's no
       cart line to swap. Either way the page's own price display is patched in place, so
       it isn't stuck showing the stale price after the popup is dismissed — and addToCart()
       creates the diamond's product at the new price. */
    async closePriceChangePopup() {
      const changed = this.priceChangedDiamond;
      this.showPriceChangePopup = false;
      this.priceChangedDiamond = null;
      if (!changed) return;

      if (changed.variantId && window.ROADiamond?.repriceCartLine) {
        this.updateWaitLoader(true, "Updating price...");
        this.diamondPriceSync = window.ROADiamond.repriceCartLine(changed).catch((error) => {
          console.error("Failed to update diamond price — original line left in place:", error);
        });
        await this.diamondPriceSync;
        this.diamondPriceSync = null;
        this.updateWaitLoader(false);
      }
      this.applyUpdatedDiamondPrice(changed);

      // Only one popup shows at a time, so the other displayed stone (toi-et-moi) may
      // still be stale — re-check both; one already corrected just matches and stays quiet.
      this.checkDisplayedDiamondPriceOnLoad(this.$props.diamond, null);
      this.checkDisplayedDiamondPriceOnLoad(this.selectedSecondDiamond, null);
    },

    /* Sets a diamond's price in place. finalPrice is the display-currency copy of
       finalPriceAud (getCurrencyWiseDiamond) and is what the second stone's total/savings
       read, so it moves with it. */
    setDisplayedDiamondPrice(diamond, price) {
      diamond.finalPriceAud = price;
      if (diamond.finalPrice !== undefined) diamond.finalPrice = price;
    },

    /* Reactively patches the price on whichever displayed diamond (primary or, for
       toi-et-moi rings, the second stone) matches the price-change entry, plus its
       sessionStorage cache, so the page's price/summary UI reflects the new price
       immediately without a page reload. Also syncs the URL (see syncUrlDiamondPrice)
       so a reload of this exact link doesn't keep re-surfacing the same stale-price
       popup once it's already been corrected. */
    applyUpdatedDiamondPrice(changed) {
      const updatedPrice = changed.updatedPrice / 100;
      if (this.$props.diamond?.diamondId === changed.diamondId) {
        this.setDisplayedDiamondPrice(this.$props.diamond, updatedPrice);
        const cached = JSON.parse(sessionStorage.getItem("selectedDiamond") || "null");
        if (cached) {
          this.setDisplayedDiamondPrice(cached, updatedPrice);
          sessionStorage.setItem("selectedDiamond", JSON.stringify(cached));
        }
      } else if (this.selectedSecondDiamond?.diamondId === changed.diamondId) {
        this.setDisplayedDiamondPrice(this.selectedSecondDiamond, updatedPrice);
        sessionStorage.setItem("selectedSecondDiamond", JSON.stringify(this.selectedSecondDiamond));
      }
      this.syncUrlDiamondPrice(changed, updatedPrice);
    },

    /* Rewrites the current URL (via router.replace — no navigation/reload) so it agrees
       with the corrected price that was just written to sessionStorage:
         - drops ?testDiamondPrice=/?testSecondDiamondPrice= entirely — their whole purpose
           was to force this one mismatch for QA; once corrected there's nothing left to
           simulate, and leaving either in would just force the same stale mismatch again on
           every reload.
         - if a ?data= payload seeded this diamond (shared link / QR code / cold open),
           patches that diamond's finalPriceAud (and finalPrice, if present) inside the
           decoded payload and re-encodes it back into the URL, so a saved/shared copy of
           this link reflects the corrected price too.
       Best-effort: any decode/encode failure just leaves the URL alone — the session and
       on-page price are already correct regardless. */
    syncUrlDiamondPrice(changed, updatedPrice) {
      try {
        const query = { ...this.$route.query };
        let touched = false;

        ["testDiamondPrice", "testSecondDiamondPrice"].forEach((key) => {
          if (query[key] !== undefined) {
            delete query[key];
            touched = true;
          }
        });

        if (query.data) {
          const isPlainJson = query.data.trim().startsWith("{");
          const decoded = decodeRingData(query.data, !isPlainJson);
          const priceStr = String(Math.round(updatedPrice));
          let decodedTouched = false;
          if (decoded?.diamond?.diamondId == changed.diamondId) {
            decoded.diamond.finalPriceAud = priceStr;
            if (decoded.diamond.finalPrice !== undefined) decoded.diamond.finalPrice = priceStr;
            decodedTouched = true;
          } else if (decoded?.secondDiamond?.diamondId == changed.diamondId) {
            decoded.secondDiamond.finalPriceAud = priceStr;
            if (decoded.secondDiamond.finalPrice !== undefined) decoded.secondDiamond.finalPrice = priceStr;
            decodedTouched = true;
          }
          if (decodedTouched) {
            const reencoded = encodeRingData(decoded, !isPlainJson);
            if (reencoded) {
              query.data = reencoded;
              touched = true;
            }
          }
        }

        if (touched) {
          this.$router.replace({ query }).catch(() => {});
        }
      } catch (error) {
        console.error("Failed to sync corrected diamond price into the URL:", error);
      }
    },

    // async addToCart(e) {

    //   this.showBtnLoader = true;
    //   this.buttonText = "Adding to cart...";
    //   e.target.style.pointerEvents = "none";

    //   // Fetch the cart to check for an engagement ring
    //  fetch(window.Shopify.routes.root + "cart.js")
    //  .then(response => response.json())
    //  .then(cart => {
    //      const hasEngagementRing = cart.items.some(item => item.product_type === "Engagement Rings");

    //      if (hasEngagementRing) {
    //          alert("Setting already exists in the cart.");
    //          this.buttonText = "Complete Purchase";
    //          e.target.style.pointerEvents = "pointer";
    //          this.showBtnLoader = false;
    //          return;
    //      }
    //     }
    //    );

    //   const shopifyProduct = {
    //     id: this.$props.setting.id,
    //     quantity: 1,
    //     properties: {
    //       ring_size: this.$props.settingSize,
    //       setting_id: this.$props.setting.id,
    //       setting_sku: this.$props.setting.sku,
    //       engraving_Text: this.engravingText,
    //       // price: parseFloat(this.$props.setting.basePrice),
    //     },
    //   };

    //   const existingProductData = await this.checkCartData(
    //     "Diamond",
    //     this.$props.diamond.diamondId
    //   );

    //   let productData;

    //   if (!existingProductData) {
    //     productData = await this.createCustomProductWithMetafields(
    //       this.$props.diamond
    //     );
    //   }

    //   if (productData || existingProductData) {
    //     const payload = {
    //       items: [
    //         {
    //           id: existingProductData ? existingProductData?.variant_id :  productData.variants[0].id,
    //           quantity: 1,
    //         },
    //         shopifyProduct,
    //       ],
    //     };

    //     fetch(window.Shopify.routes.root + "cart/add.js", {
    //       method: "POST",
    //       headers: {
    //         "Content-Type": "application/json",
    //       },
    //       body: JSON.stringify(payload),
    //     })
    //       .then((response) => response.json())
    //       .then((response) => {
    //         console.log("Cart Response:", response);
    //         this.buttonText = "Added to Cart!";
    //           sessionStorage.removeItem("selectedSetting");
    //           sessionStorage.removeItem("selectedDiamond");
    //           sessionStorage.removeItem("selectedSettingOptions");
    //           sessionStorage.removeItem("stepData");
    //           sessionStorage.removeItem("stickyBarItems");
    //           window.location.href = "/cart";
    //       })
    //       .catch((error) => {
    //         console.error("Add to Cart Error:", error);
    //         this.buttonText = "Add to Cart";
    //       })
    //       .finally(() => {
    //         this.showBtnLoader = false;
    //         e.target.style.pointerEvents = "auto";
    //       });
    //   } else {
    //     alert("Product not added.");
    //   }

    //   this.showBtnLoader = false;
    //   e.target.style.pointerEvents = "auto";
    // },

    // The exact ring this order is for, as a tdp3dstudio "view" URL — same
    // params/format as the live embed on the product page
    // (snippets/product-3d-preview.liquid: currentRing3dUrl/buildSelection),
    // rebuilt here from data this component already has because the Ring
    // Builder page never shares a DOM with a product page's 3D viewer (it's a
    // separate template — there is no window.PC3D-style state to read).
    // window.RB_3D_CONFIG (layout/theme.liquid, ring-builder pages only) is
    // the store's DEFAULT embed; a setting with its own custom.ring_3d_embed_id
    // metafield override is not visible to this component and is not applied
    // here — an acceptable gap for a hidden, backend-only field.
    // ENCRYPTED, for the same reason it is stored at all: this url leaves the
    // storefront. Spelling out the band, shape, carat, prong and metal in an
    // order email and the shop's admin is exactly what product-3d-preview.liquid
    // stopped doing — sealRing3dUrl mints the token on the viewer's server
    // (the AES key never leaves it) with the engraving baked INSIDE it, and
    // resolves null on a failed mint rather than falling back to the plain,
    // readable url this used to build. A cart line with no preview link is a
    // worse experience than one with a link, but it is not a leak.
    async sealRing3dUrl({ sku, shape, carat, metal, prongType, engraving, gem }) {
      try {
        var band = (sku || "").split("-")[0];
        if (!band || !window.PC3DLink) return null;
        var cfg = (window.RB_3D_CONFIG || {});
        var baseUrl = cfg.baseUrl || "https://i3dstudio.vercel.app";
        var embedId = cfg.embedId || "";
        if (!embedId) return null;
        // Mirrors metalToken() in product-3d-preview.liquid: carat doesn't
        // change the 3D model, only colour does, so every karat maps to the
        // 18k token; platinum maps to its own preset.
        var metalStr = (metal || "").toLowerCase();
        var metalToken;
        if (metalStr.indexOf("platinum") !== -1 || metalStr.indexOf("plat") !== -1) {
          metalToken = "platinum_950";
        } else {
          var color = metalStr.indexOf("white") !== -1 ? "white" : (metalStr.indexOf("rose") !== -1 ? "rose" : "yellow");
          metalToken = color + "_gold_18k";
        }
        var params = {
          band: band,
          shape: shape || "Round",
          carat: carat || "",
          "prong-style": (prongType || "round").toLowerCase(),
          metal: metalToken,
        };
        // Same per-product override product-3d-preview.liquid reads (ring_3d_gem
        // metafield) — sent only when the merchant has actually set one; a gem
        // param outranks the embed's own dashboard default, so it must never be
        // invented here.
        if (gem) params.gem = gem;
        var engText = (engraving || "").trim();
        if (engText) params.eng = engText;
        return await window.PC3DLink.seal(baseUrl, embedId, params);
      } catch (e) {
        return null;
      }
    },

    /* /cart/add.js through the shared retry policy in window.ROACart.add
       (snippets/cart-fetch-dedupe.liquid):
         429 Too Many Requests -> wait Retry-After / exponential backoff + jitter,
                                  at most 4 attempts and ~15s in total, with the
                                  button reading "Busy, retrying..." meanwhile;
         422 "sold out"        -> the product created a moment ago has not reached
                                  the storefront yet: a few 2s retries;
         anything else         -> fail at once (the old catch-all retried every
                                  error five times, which is what turned one 429
                                  into six requests).
       Rejects with an Error whose .friendlyMessage, when set, is what to show. */
    async addToCartWithRetry(payload) {
      if (window.ROACart && typeof window.ROACart.add === "function") {
        return window.ROACart.add(payload, {
          url: window.Shopify.routes.root + "cart/add.js",
          onRetry: (info) => {
            this.buttonText =
              info.reason === "busy" ? "Busy, retrying..." : "Adding to cart...";
          },
        });
      }

      // No wrapper on the page: one plain attempt, no retry.
      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.description || data?.message || "Unable to add to cart.");
      }
      return data;
    },
    async addToCart(e) {
      this.showBtnLoader = true;
      this.buttonText = "Adding to cart...";
      e.target.disabled = true;

      try {
        // A price update started by closing the price-change popup must land first,
        // or the checks below would read the cart mid-swap.
        if (this.diamondPriceSync) await this.diamondPriceSync;

        // ---- Read the cart ONCE for the whole pre-flight ----
        // The price check, the engagement-ring conflict check and both
        // checkCartData() calls below each used to fetch /cart.js on their own.
        // Nothing writes to the cart until the single /cart/add.js at the end,
        // so one read is all of them need.
        const cart = await this.readCart();

        // ---- Re-check diamond availability before doing anything ----
        if (window.ROADiamond) {
          const stones = [this.$props.diamond];
          if (
            this.$props.setting?.tags?.includes("toi-et-moi") &&
            this.selectedSecondDiamond &&
            this.selectedSecondDiamond.diamondId
          ) {
            stones.push(this.selectedSecondDiamond);
          }
          for (const stone of stones) {
            const status = await window.ROADiamond.checkAvailability({
              diamondId: stone.diamondId,
              diamondType: stone.diamondType,
            });
            if (status === "soldout") {
              window.ROADiamond.clearBuilderSelection(stone.diamondId);
              this.soldOutDiamond = stone;
              this.showSoldOutPopup = true;
              this.buttonText = "Complete Purchase";
              this.showBtnLoader = false;
              e.target.disabled = false;
              return;
            }
          }

          // ---- Re-check every stone's price against the diamond detail API ----
          // A stone already in the cart is checked by its cart line (re-adding the same
          // diamond from an earlier visit shouldn't silently commit to a stale price); one
          // that isn't, by the price its new product is about to be created at. Either
          // mismatch surfaces the same "Diamond Price Has Changed" popup.
          const changedEntry = await this.findChangedDiamondPrice(stones, true, cart);
          if (changedEntry) {
            this.priceChangedDiamond = changedEntry;
            this.showPriceChangePopup = true;
            this.buttonText = "Complete Purchase";
            this.showBtnLoader = false;
            e.target.disabled = false;
            return;
          }
        }

        // ---- Band Width & Matching Band from sessionStorage ----
        let selectedSetting = JSON.parse(
          sessionStorage.getItem("selectedSetting") || "{}"
        );
        let bandWidth = selectedSetting.bandWidth || "";
        let prongType = selectedSetting.prongType || "";
        let matchingBand = selectedSetting.matching_band || null;

        // Engagement ring already in the cart? (the cart read at the top)
        const conflictItems = cart.items.filter(
          (item) => item.product_type === "Engagement Rings"
        );

        if (conflictItems.length && !this._skipConflictCheck) {
          this.cartConflictItems = cart.items;
          this.newRingForModal = this.selectedSetting
            ? {
                image: this.selectedSetting.main_image,
                name: this.selectedSetting.name,
                price: this.selectedSetting.price,
                currency: this.selectedSetting.currency || "$",
              }
            : null;
          this._pendingAddToCartEvent = e;
          this.showCartConflictModal = true;
          this.buttonText = "Complete Purchase";
          this.showBtnLoader = false;
          e.target.disabled = false;
          return;
        }
        this._skipConflictCheck = false;

        // ---- Prepare engagement ring object ----
        const ring3dUrl = await this.sealRing3dUrl({
          sku: this.$props.setting?.sku,
          shape: this.$props.diamond?.shape,
          carat: this.$props.diamond?.caratWeight,
          // `this.setting` is bound to the root's `setting.data` — settingMetal is a
          // SIBLING field on the root's raw setting object, never merged into `.data`,
          // so `this.setting.settingMetal` was always empty here (silently defaulting
          // the cart's 3D-preview URL to yellow gold regardless of the real metal).
          // `this.settingMetal` is the correctly-bound prop (see props[] above).
          metal: this.settingMetal || "",
          prongType: prongType,
          engraving: this.engravingText,
          gem: this.$props.setting?.ring3dGem,
        });
        const shopifyProduct = {
          id: this.$props.setting.id,
          quantity: 1,
          properties: {
            ring_size: this.$props.settingSize,
            setting_id: this.$props.setting.id,
            setting_sku: this.$props.setting.sku,
            engraving_Text: this.engravingText,
            band_width: bandWidth,
            prong_type: prongType,
            // Underscore-prefixed: Shopify keeps it off the cart, checkout and
            // order confirmation, but the cart line and the merchant's order
            // view can still read the exact ring the shopper configured.
            ...(ring3dUrl ? { _3d_preview_url: ring3dUrl } : {}),
          },
        };

        let isToiMoiTest = this.$props.setting.tags.includes("toi-et-moi");
        let secondDiamond = this.selectedSecondDiamond;

        // ---- Check First Diamond ----
        const existingProductData = await this.checkCartData(
          this.diamondTypeToProductType(this.$props.diamond.diamondType),
          this.$props.diamond.diamondId,
          cart
        );

        let productData;

        // If no existing Diamond, create a custom product with metafields
        if (!existingProductData) {
          productData = await this.createCustomProductWithMetafields(
            this.$props.diamond
          );
        }

        if (productData?.handle) {
          // The add below can only bounce (422 "sold out") against a product the
          // storefront cannot see yet — stop with a clear message instead.
          if (!(await this.checkStorefrontWithRetry(productData.handle))) {
            throw this.storefrontNotReadyError();
          }
        }


        // ---- Handle Toi-Moi Second Diamond ----
        let secondDiamondData = null;
        let existingSecondDiamondData = null;

        if (isToiMoiTest && secondDiamond) {
          existingSecondDiamondData = await this.checkCartData(
            this.diamondTypeToProductType(secondDiamond.diamondType),
            secondDiamond.diamondId,
            cart
          );

          if (!existingSecondDiamondData) {
            secondDiamondData = await this.createCustomProductWithMetafields(
              secondDiamond
            );
          }

          if (secondDiamondData?.handle) {
            if (!(await this.checkStorefrontWithRetry(secondDiamondData.handle))) {
              throw this.storefrontNotReadyError();
            }
          }
        }

        // ---- Prepare payload items ----
        if (productData || existingProductData) {
          const items = [];

          // Add first diamond
          const tempData = this.$props.diamond;
          items.push({
            id: existingProductData
              ? existingProductData.variant_id
              : productData.variant.id,
            quantity: 1,
            properties: {
              stock_no: tempData.stockNumber,
              certificate_number: tempData.certificateNumber,
              sku: tempData.diamondId,
              stone_shape: tempData.shape,
              laboratory: tempData.lab,
              certificate: tempData.certificateFile,
              carat_weight: tempData.caratWeight,
              clarity: tempData.clarity,
              color: tempData.color,
              stone_type:
                tempData?.diamondType == "lab"
                  ? "Lab Diamonds"
                  : tempData?.diamondType == "lab_color"
                    ? "Lab Colour Diamonds"
                    : tempData?.diamondType == "white"
                      ? "Natural Diamonds"
                      : tempData?.diamondType == "white_color"
                        ? "Natural Colour Diamonds"
                        : tempData?.diamondType,
              cut: tempData.cut,
              polish: tempData.polish,
              symmetry: tempData.symmetry,
              fluorescence: tempData.flourIntensity,
            },
          });

          // Add engagement ring
          items.push(shopifyProduct);

          // Add second diamond if Toi-Moi
          if (
            isToiMoiTest &&
            (secondDiamondData || existingSecondDiamondData)
          ) {
            const tempData2 = secondDiamond;
            items.push({
              id: existingSecondDiamondData
                ? existingSecondDiamondData.variant_id
                : secondDiamondData.variant.id,
              quantity: 1,
              properties: {
                stock_no: tempData2.stockNumber,
                certificate_number: tempData2.certificateNumber,
                sku: tempData2.diamondId,
                stone_shape: tempData2.shape,
                laboratory: tempData2.lab,
                certificate: tempData2.certificateFile,
                carat_weight: tempData2.caratWeight,
                clarity: tempData2.clarity,
                color: tempData2.color,
                stone_type:
                  tempData2?.diamondType == "lab"
                    ? "Lab Diamonds"
                    : tempData2?.diamondType == "lab_color"
                      ? "Lab Colour Diamonds"
                      : tempData2?.diamondType == "white"
                        ? "Natural Diamonds"
                        : tempData2?.diamondType == "white_color"
                          ? "Natural Colour Diamonds"
                          : tempData2?.diamondType,
                cut: tempData2.cut,
                polish: tempData2.polish,
                symmetry: tempData2.symmetry,
                fluorescence: tempData2.flourIntensity,
              },
            });
          }

          // ---- Add matching band automatically ----
          if (Object.keys(this.matchingBand || {}).length > 0) {
            items.push({
              id: this.matchingBand.id,
              quantity: 1,
              properties: {
                parent_setting_id: this.$props.setting.id,
              },
            });
          }

          const payload = { items };

          const button = e.target;

          button.disabled = true;
          this.showBtnLoader = true;
          this.buttonClicked = true;



          try {
            await this.addToCartWithRetry(payload, button);

            this.buttonText = "Added to Cart!";

            /* The ring is in the cart now, so this is the first moment the
               gift's threshold can actually be judged. Settle it here rather
               than leaving it to the cart page's one-shot updateGift(), which
               is what used to let an opted-in shopper check out with no gift.
               (snippets/free-gift.liquid) */
            if (typeof window.ensureFreeGiftInCart === "function") {
              await window.ensureFreeGiftInCart({ navigatingToCart: true });
            }

            [
              "selectedSecondDiamond",
              "searchingSecondDiamond",
              "selectedSetting",
              "selectedDiamond",
              "selectedSettingOptions",
              "stepData",
              "stickyBarItems",
              "selectedMatchingBand",
            ].forEach((key) => sessionStorage.removeItem(key));

            window.location.href = "/cart";
          } catch (error) {
            console.error("Add to Cart Error:", error);

            this.buttonText = "Complete Purchase";
            this.showTooltip = true;
            // A 429 that never cleared carries its own wording (ROACart.busyMessage).
            this.tooltipText =
              error?.friendlyMessage || "Something went wrong, please try again.";
            this.tooltipClass = "";

            setTimeout(() => {
              this.showBtnLoader = false;
              e.target.disabled = false;
              this.buttonText = "Complete Purchase";
            }, 2500);
          } finally {
            this.showBtnLoader = false;
            this.buttonClicked = false;
            button.disabled = false;
          }

        } else {
          this.showTooltip = true;
          this.tooltipText = "Something went wrong, please try again.";
          this.tooltipClass = "";
        }
      } catch (error) {
        console.error("Add to Cart Error:", error);
        // Covers the cart read, product creation and the storefront wait — tell the
        // shopper rather than silently resetting the button.
        this.showTooltip = true;
        this.tooltipText =
          error?.friendlyMessage || "Something went wrong, please try again.";
        this.tooltipClass = "";
      } finally {
        this.showBtnLoader = false;
        e.target.disabled = false;
        this.buttonText = "Complete Purchase";
        setTimeout(() => {
          this.showTooltip = false;
        }, 2000);
      }
    },

    copyShareableLink() {
      navigator.clipboard.writeText(window.location.href).then(() => {
        this.showShareLinkCopied = true;
        setTimeout(() => { this.showShareLinkCopied = false; }, 2500);
      });
    },
    closeCartConflictModal() {
      this.showCartConflictModal = false;
      this.cartConflictItems = [];
      this._pendingAddToCartEvent = null;
    },

    submitDropHint() {
      this.dahErrors = {};
      var valid = true;
      if (!this.dahSenderName.trim()) { this.dahErrors.senderName = true; valid = false; }
      if (!this.dahRecipientName.trim()) { this.dahErrors.recipientName = true; valid = false; }
      if (!this.dahRecipientEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.dahRecipientEmail)) { this.dahErrors.recipientEmail = true; valid = false; }
      if (!valid) return;

      this.dahSubmitting = true;
      var self = this;
      var cartUrl = window.location.href;

      var params = new URLSearchParams({
        EMAIL: this.dahRecipientEmail,
        FNAME: this.dahSenderName,
        LNAME: this.dahRecipientName,
        MMERGE6: cartUrl,
        tags: 6521826,
        'b_bd5edca466a37b5cb7a5021e2_feaed77a7d': ''
      });

      var cbName = 'dahMcCb_' + Date.now();
      var scriptSrc = 'https://ringsofaustralia.us17.list-manage.com/subscribe/post-json'
        + '?u=bd5edca466a37b5cb7a5021e2&id=feaed77a7d&f_id=00196ae0f0&'
        + params.toString() + '&c=' + cbName;

      var fired = false;
      window[cbName] = function() {
        if (fired) return; fired = true;
        delete window[cbName];
        var s = document.getElementById('dah-mc-script');
        if (s) s.remove();
        self.dahSubmitting = false;
        self.dahSent = true;
      };
      setTimeout(function() { if (!fired) window[cbName](); }, 6000);

      var script = document.createElement('script');
      script.id = 'dah-mc-script';
      script.src = scriptSrc;
      document.head.appendChild(script);
    },

    formatMetalName(metal) {
      if (!metal) return '';
      return metal.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },

    getMetalComposition(metal) {
      if (!metal) return [];
      const m = metal.toLowerCase();
      const CIRC = 87.96; // 2π×r = 2π×14 ≈ 87.96

      const build = (items) => {
        let offset = 0;
        return items.map(item => {
          const dash = (item.pct / 100) * CIRC;
          const gap  = CIRC - dash;
          const seg  = { ...item, dash: dash + ' ' + gap, offset: -offset };
          offset += dash;
          return seg;
        });
      };

      if (m.includes('yellow')) {
        if (m.includes('18k')) return build([
          { name: 'Gold',   pct: 75.0, color: '#E8B84B' },
          { name: 'Copper', pct: 12.5, color: '#B56A2E' },
          { name: 'Silver', pct: 12.5, color: '#C0C0C0' },
        ]);
        if (m.includes('14k')) return build([
          { name: 'Gold',   pct: 58.5, color: '#E8B84B' },
          { name: 'Copper', pct: 30.5, color: '#B56A2E' },
          { name: 'Silver', pct: 6.1,  color: '#C0C0C0' },
          { name: 'Zinc',   pct: 4.7,  color: '#A8A8A8' },
        ]);
        return build([
          { name: 'Gold',   pct: 37.5, color: '#E8B84B' },
          { name: 'Copper', pct: 42.0, color: '#B56A2E' },
          { name: 'Silver', pct: 20.5, color: '#C0C0C0' },
        ]);
      }
      if (m.includes('rose')) {
        if (m.includes('18k')) return build([
          { name: 'Gold',   pct: 75.0,  color: '#E8B84B' },
          { name: 'Copper', pct: 22.25, color: '#B56A2E' },
          { name: 'Silver', pct: 2.75,  color: '#C0C0C0' },
        ]);
        if (m.includes('14k')) return build([
          { name: 'Gold',   pct: 58.5, color: '#E8B84B' },
          { name: 'Copper', pct: 35.7, color: '#B56A2E' },
          { name: 'Silver', pct: 5.8,  color: '#C0C0C0' },
        ]);
        return build([
          { name: 'Gold',   pct: 37.5, color: '#E8B84B' },
          { name: 'Copper', pct: 56.5, color: '#B56A2E' },
          { name: 'Silver', pct: 6.0,  color: '#C0C0C0' },
        ]);
      }
      if (m.includes('white')) {
        if (m.includes('18k')) return build([
          { name: 'Gold',      pct: 75.0, color: '#E8B84B' },
          { name: 'Palladium', pct: 15.0, color: '#9E9EAE' },
          { name: 'Silver',    pct: 10.0, color: '#C0C0C0' },
        ]);
        if (m.includes('14k')) return build([
          { name: 'Gold',      pct: 58.5, color: '#E8B84B' },
          { name: 'Silver',    pct: 32.2, color: '#C0C0C0' },
          { name: 'Palladium', pct: 9.3,  color: '#9E9EAE' },
        ]);
        return build([
          { name: 'Gold',      pct: 37.5, color: '#E8B84B' },
          { name: 'Silver',    pct: 52.0, color: '#C0C0C0' },
          { name: 'Palladium', pct: 10.5, color: '#9E9EAE' },
        ]);
      }
      if (m.includes('platinum')) return build([
        { name: 'Platinum', pct: 95.0, color: '#9E9EAE' },
        { name: 'Cobalt',   pct: 5.0,  color: '#7070A0' },
      ]);
      if (m.includes('silver')) return build([
        { name: 'Silver',  pct: 92.5, color: '#C0C0C0' },
        { name: 'Copper',  pct: 7.5,  color: '#B56A2E' },
      ]);
      return build([{ name: metal, pct: 100, color: '#C0C0C0' }]);
    },

    getMetalColor(metal) {
      if (!metal) return '#ccc';
      const m = metal.toLowerCase();
      if (m.includes('yellow')) return 'linear-gradient(135deg,#e8c96a,#c9922a)';
      if (m.includes('rose'))   return 'linear-gradient(135deg,#f0b8a0,#c97050)';
      if (m.includes('white'))  return 'linear-gradient(135deg,#f0f0f0,#c8c8c8)';
      if (m.includes('platinum')) return 'linear-gradient(135deg,#e8e8ec,#a8a8b8)';
      if (m.includes('silver'))   return 'linear-gradient(135deg,#f0f0f0,#b0b0b8)';
      return '#ccc';
    },

    copyHintLink() {
      var self = this;
      var url = window.location.href;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function() {
          self.dahCopied = true;
          setTimeout(function() { self.dahCopied = false; }, 3000);
        });
      } else {
        var ta = document.createElement('textarea');
        ta.value = url; ta.style.cssText = 'position:fixed;opacity:0;';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); } catch(e) {}
        document.body.removeChild(ta);
        self.dahCopied = true;
        setTimeout(function() { self.dahCopied = false; }, 3000);
      }
    },

    async replaceCartItems() {
      this.showCartConflictModal = false;
      this.cartConflictItems = [];

      try {
        await fetch(window.Shopify.routes.root + "cart/clear.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
      } catch (err) {
        console.error("Failed to clear cart:", err);
      }

      // Re-run addToCart, skipping the conflict check this time
      this._skipConflictCheck = true;
      const savedEvent = this._pendingAddToCartEvent;
      this._pendingAddToCartEvent = null;
      if (savedEvent) {
        savedEvent.target.disabled = false;
        this.addToCart(savedEvent);
      }
    },

    viewListingPage(pageType, diamondType = "white") {
      var type = pageType;
      var firstPage = "";
      if (sessionStorage.getItem("firstPage")) {
        firstPage = sessionStorage.getItem("firstPage");
      }
      if (type == "setting") {
        if (firstPage == "settingPage") {
          this.$router.push({
            name: "chooseSetting",
            query: { pageType: "setting" },
          });
          return;
        }
        this.$router.push({ name: "chooseSetting" });
      } else if (type == "diamond") {
        var diamondType = getDiamondListingType(diamondType);
        if (firstPage == "diamondPage") {
          this.$router.push({
            name: "chooseDiamonds",
            params: { diamondType },
            query: { pageType: "diamond" },
          });
          return;
        }
        this.$router.push({ name: "chooseDiamonds", params: { diamondType } });
      }
    },
  },
};

var graphicsMetaData = {
  caratText:
    "The carat is the standard unit of measurement used to indicate the weight of a diamond. Carat is often confused with size even though it is actually a measure of weight. One carat equals 200 milligrams or 0.2 grams. Remember that while the measurements below are typical, every diamond is unique. One carat (1 ct) equals 0.20 grams and is divided into 100 points (half a carat – 0.50 ct – diamond can be described as a 50 points diamond).The scale below illustrates the typical size relationship between diamonds of increasing carat weights.",
  cutText:
    "Nothing is more impactful on a precious stone's brilliance than its cut. The cut refers to the angles and proportions of a diamond. Known in the industry as ‘fire’, referring to the coloured light reflected, and ‘brilliance’ for the uncoloured light. The cut of a diamond - its form and finish, its depth and width, the uniformity of the facets - determines its beauty. The skill with which a diamond is cut determines how well it reflects and refracts light. A diamond should be cut proportionally, neither very shallow nor very deep, given its dimensions. A diamond’s cut is its most important characteristics and a measure of its apparent beauty.",
  colorText:
    "A diamond’s colour is one of its most significant characteristics, enlisted as a fundamental measure of quality. The diamond colour scale begins at D (colourless) and ends at Z (light yellow). Highest quality diamond stones have no hue in their sparkle and that is their classic splendour, admired for ages. Colourless diamonds allow more light to pass through than a coloured diamond, releasing more sparkle and fire.",
  clarityText:
    "A diamond's clarity refers to the presence of impurities on and within the stone. When diamonds are formed naturally under extreme heat and pressure, imperfections in the crystal structure can form and tiny traces of natural elements are almost always trapped inside called inclusions. The diamond with highest quality on clarity grading is the one with absence of any imperfections which could possibly occur in the stone.",
  DColor:
    "D colour diamonds are absolutely colourless, the rarest gemstones to exist. The most expensive colour grade",
  EColor:
    "E colour range is the second highest quality range of diamonds while examining their transparency. These are rare diamonds and the very slight requires professional expertise to detect any dim tone.",
  FColor:
    'F colour detected by an expert gemologist, but still considered a "colorless" grade -- a high-quality diamond.',
  GColor:
    "G graded diamonds with noticeable tint is detectable for experts with a keen eye when compared to diamonds of better grades, but offers excellent value.",
  HColor:
    "H coloured diamonds are near colourless diamonds which still appear totally white or colourless if they are not compared side by side with higher colour graded stones.They are almost completely hue free.",
  IColor:
    "I Colour diamonds are very slightly tinted diamonds and noticeable when compared to diamonds of better grades, but offers great value for money.",
  JColor:
    "J coloured diamonds range have trivial hue. The tint is very ignorable.",
  KColor:
    "K coloured diamonds are slightly tinted diamonds, however, once set in jewellery, especially in yellow gold, it is harder to see the slight yellow tint which the K grade produces.",
  LColor:
    "L colour – We don’t carry lower clarity grades however can be made available on special request. Gemsake currently offers diamonds in the higher range of D-J colour, as these are the only grades we recommend.",
  I1Clarity:
    "The Included Diamonds with I1-I3 as the lowest clarity grade on the scale.The imperfections in these diamonds are very much visible to the naked eye.",
  SI2Clarity:
    "The slightly included diamonds, considered to be fine quality, having visible imperfections under magnification range of 10x",
  SI1Clarity:
    "The slightly included diamonds, considered to be fine quality, having visible imperfections under magnification range of 10x",
  VS2Clarity:
    "The very slightly included grade of diamonds which have visible imperfections on the uncovered eye.",
  VS1Clarity:
    "The very slightly included grade of diamonds which have visible imperfections on the uncovered eye.",
  VVS2Clarity:
    "This grade range includes the diamonds which are very, very slightly included. It is almost impossible to detect inclusions under 10x range magnification",
  VVS1Clarity:
    "This grade range includes the diamonds which are very, very slightly included. It is almost impossible to detect inclusions under 10x range magnification.",
  IFClarity:
    "The diamonds in this category are completely flawless with no internal or external inclusions of any kind. These are the rarest diamonds.",
  "excellent cut":
    "It is an exceptionally uncommon cut which reflects roughly all the light that enters the precious stone. The excellent Cut refers to the topmost three percent quality diamond stones.",
  "very good cut":
    "This cut evaluation is a representation of around fifteen percent of the top quality jewels which reflects just about as much light as falls into the precious stone, one grade lower than the Excellent cut’s reflection which is almost absolute.",
  "good cut":
    "Reflects most light that enters, represents around a quarter of diamonds. Good quality cut to optimize the size without sacrificing quality or beauty. In monetary terms the Good Cut diamonds are far less costly than the Very Good Cut ones.",
  "fair cut":
    "Fair Cut diamonds are just about reasonable, speaks to thirty five percent of the precious stones. This is the lower quality cut approved for jewellery, by experts.",
  MColor:
    "Diamonds with M Colour have a subtle tint that becomes noticeable to the trained eye. M-grade diamonds have a lovely quality even with a slight Colour presence, especially when set in jewelry.",
  NColor:
    "N Colour diamonds are slightly more noticeable than their M-grade equivalents, with a subtle hint of Colour. These diamonds retain their elegant appearance despite their subtle tint, especially when set in settings that accentuate their inherent beauty.",
};

/* final ring detail page */
const FinalRingDetailPageComp = {
  rapnetRequestDiamonds: [],
  components: {
    BreadcrumbsComp,
    FinalRingDetailTable,
    FinalRingMediaGallery,
    FinalRingMetaData,
    DetailSpinnerComp,
    HeaderStepsComp,
    StickyBarComp,
    DiamondCaratGraphicComp,
    DiamondCutGraphicComp,
    DiamondColorGraphicComp,
    DiamondClarityGraphicComp,
    SocialIconComp,
    DiamondSoldOutPopup,
  },
  data() {
    return {
      setting: {
        data: {},
        attributes: {},
        settingSize: "Select Ring Size",
        settingMetal: "",
      },
      diamond: {
        data: {},
      },
      pageMedia: [],
      loading: false,
      breadcrumbs: [],
      dataNotFound: false,
      showSoldOutPopup: false,
      soldOutDiamond: null,
      showDiamondRemovedPopup: false,
      removedDiamondType: "",
      /* Blocks the dataNotFound auto-redirect until the sold-out check resolves,
         so a sold-out diamond shows the popup instead of a redirect. */
      soldOutCheckPending: true,
      /* Blocks the dataNotFound auto-redirect until a fresh setting/diamond fetch
         (getSettingData/getDiamondData/loadOnlySetting/loadFinalRing) has settled,
         so a first-time paste of a final-ring URL isn't redirected away while its
         own network requests are still in flight. Mount() flips this false once it
         determines the page needs no fetch (both came from sessionStorage). */
      settingDiamondFetchPending: true,
      defaultSettingCountry: "AUD",
      finalSettingSize: "Select Ring Size",
      stepData: (function() {
        var fp = sessionStorage.getItem("firstPage");
        if (fp == "diamondPage") {
          return { firstStep: { headerTitle: "DIAMOND" }, secondStep: { headerTitle: "SETTING" }, thirdStep: { headerTitle: "Ring" } };
        }
        return { firstStep: { headerTitle: "SETTING" }, secondStep: { headerTitle: "DIAMOND" }, thirdStep: { headerTitle: "Ring" } };
      })(),
      totalSteps: 3,
      pageStickyItems: [],
      coupon_bangle_img: `${baseAssetsUrl}/bangles.png`,
      alexis: `${baseAssetsUrl}/alexis.png`,
      settingMetalList: [],
      changeInMetalLoading: false,
      getDiamond: false,
      getSetting: false,
      redirectionPossible: false,
      redirectPage: "",
      matchingBandData: null,
      matchingBandVariant: null,
      showMatchingBandSection: false,
      matchingBandLoading: false,
      isDesktopView: window.innerWidth >= 992,
    };
  },
  delimiters: ["[[", "]]"],
  template: `#final-ring-detail-page`,
  mounted() {
    // A link carrying the full setting/diamond payload (see encodeRingData in
    // ring-builder-common-components.js) lets a cold open reproduce fields the
    // Storefront API can't refetch (metafield-backed setting video, band width,
    // prong type). Seed sessionStorage from it before the normal branching below
    // runs, so every existing code path just sees a populated cache.
    if (this.$route.query.settingVideo) {
      sessionStorage.setItem("selectedSettingVideo", this.$route.query.settingVideo);
    }
    const encodedData = this.$route.query.data;
    // trimSettingForUrl() (ring-builder-common-components.js) strips images/media
    // from the setting before encoding to keep the URL under request-size limits —
    // when a decode seeds selectedSetting below, it's missing image_gallery/
    // main_image/featured_image, so the branching further down must be forced
    // through a setting refetch (which merges fresh images back onto this cached
    // object) rather than taking the "everything cached" fast path.
    let decodedSettingNeedsImageRefetch = false;
    if (encodedData) {
      const isPlainJson = encodedData.trim().startsWith("{");
      const decoded = decodeRingData(encodedData, !isPlainJson);
      // Some callers (e.g. the plain-product-page "Choose This Setting" button,
      // snippets/ring-builder-choose-setting-button.liquid) intentionally encode only
      // a small identity+config stub ({id, settingSize, settingMetal, settingVideo} /
      // {diamondId, diamondType}) instead of the full setting/diamond objects, to stay
      // under nginx's request-line limit on a same-tab redirect — relying on
      // sessionStorage (already populated before that redirect) for the rest, and only
      // needing this stub for a cold/new-tab open (where sessionStorage is empty and
      // these fields can't be refetched from the product API). Detect "full" by a field
      // only ever present on the rich object (name/sku for setting, shape for diamond),
      // not just key count, so this stub is never mistaken for a full object — trusting
      // it enough to overwrite sessionStorage would clobber the already-complete cached
      // data (tags, attributes, images, etc.) on a same-tab redirect.
      const decodedSettingIsFull = !!decoded?.setting && (!!decoded.setting.name || !!decoded.setting.sku);
      const decodedDiamondIsFull = !!decoded?.diamond && !!decoded.diamond.shape;
      if (decodedSettingIsFull && decoded?.setting?.id == this.$route.query.settingId) {
        sessionStorage.setItem("selectedSetting", JSON.stringify(decoded.setting));
        decodedSettingNeedsImageRefetch = !decoded.setting.image_gallery;
        try {
          if (decoded.setting.settingVideo) {
            sessionStorage.setItem("selectedSettingVideo", decoded.setting.settingVideo);
          }
        } catch (e) {}
      }
      if (decodedDiamondIsFull && decoded?.diamond?.diamondId == this.$route.query.diamondId) {
        sessionStorage.setItem("selectedDiamond", JSON.stringify(decoded.diamond));
      }
    }
    if (!!sessionStorage.getItem("redirection")) {
      this.redirectPage = sessionStorage
        .getItem("redirection")
        .includes("-diamonds")
        ? "DIAMOND"
        : "SETTING";
    }
    this.redirectionPossible = !!sessionStorage.getItem("redirection");
    let pendingFetch = null;
    if (
      sessionStorage.getItem("selectedSetting") != null &&
      sessionStorage.getItem("selectedDiamond") != null
    ) {
      const selectedSetting = JSON.parse(
        sessionStorage.getItem("selectedSetting")
      );
      const selectedDiamond = JSON.parse(
        sessionStorage.getItem("selectedDiamond")
      );

      if (
        selectedSetting?.id != this.$route.query.settingId &&
        selectedDiamond.diamondId != this.$route.query.diamondId
      ) {
        pendingFetch = this.loadFinalRing(
          this.$route.query.settingId,
          this.$route.query.diamondId
        );
      } else if (
        selectedSetting?.id == this.$route.query.settingId &&
        selectedDiamond.diamondId != this.$route.query.diamondId
      ) {
        this.loading = true;
        this.handleSetting(selectedSetting);
        pendingFetch = this.loadDiamond(this.$route.query.diamondId);
      } else if (
        (selectedSetting?.id != this.$route.query.settingId &&
          selectedDiamond.diamondId == this.$route.query.diamondId) ||
        (decodedSettingNeedsImageRefetch &&
          selectedSetting?.id == this.$route.query.settingId &&
          selectedDiamond.diamondId == this.$route.query.diamondId)
      ) {
        this.loading = true;
        this.handleDiamond(selectedDiamond);
        pendingFetch = this.loadOnlySetting(this.$route.query.settingId);
      } else {
        this.handleDiamond(selectedDiamond);
        this.handleSetting(selectedSetting);
        this.handleMetaList(
          selectedSetting.attributes.Engagement_Ring_Metal_Type
        );
        this.handleFinalRingOptions();
        this.handleMediaGallery(
          selectedSetting.image_gallery,
          selectedDiamond.diamondImage
        );

        this.handleBreadcrumbs();
        this.handleMetaData();
        this.handleStepData();
        this.handleStickyBarItems();
      }
    } else if (
      sessionStorage.getItem("selectedSetting") != null &&
      sessionStorage.getItem("selectedDiamond") == null
    ) {
      // Setting exists but diamond was removed (user clicked "Change" on diamond)
      const selectedSetting = JSON.parse(
        sessionStorage.getItem("selectedSetting")
      );
      this.handleSetting(selectedSetting);
      pendingFetch = this.getDiamondData(
        this.$route.query.diamondId,
        this.$route.query.diamondType
      );
    } else {
      pendingFetch = Promise.allSettled([
        this.getDiamondData(
          this.$route.query.diamondId,
          this.$route.query.diamondType
        ),
        this.getSettingData(this.$route.query.settingId),
      ]).then(() => this.reconcileDataNotFound());
    }
    if (pendingFetch && typeof pendingFetch.finally === "function") {
      pendingFetch.finally(() => {
        this.settingDiamondFetchPending = false;
      });
    } else {
      // Both diamond and setting already came from sessionStorage — nothing to wait on.
      this.settingDiamondFetchPending = false;
    }
    handlePageBodyClass("add", "pc-ringbuilder-final-ring-detail-page");
    this.handleViewportChange();
    window.addEventListener("resize", this.handleViewportChange);
    /* A matching band added on this page only ever lived in sessionStorage —
       a cold-pasted URL carrying matchingBandId re-fetches it so the band
       (and its price/discount) survives a fresh open, matching everything else. */
    const matchingBandId = this.$route.query.matchingBandId;
    if (matchingBandId && !sessionStorage.getItem("selectedMatchingBand")) {
      this.fetchMatchingBandData(matchingBandId).finally(() => {
        this.initMatchingBandSection();
        // initMatchingBandSection() only populates matchingBandVariant for the
        // "offer to add" prompt — it short-circuits here since the band is
        // already in sessionStorage (added, not just offered), so the gallery
        // thumbnail has to be pulled from the freshly-fetched cache entry directly.
        const addedBand = safeParseJSON("selectedMatchingBand");
        if (addedBand?.image) {
          this.addMatchingBandImageToGallery(addedBand.image);
        }
        const metaDataRefs = [this.$refs.metaData, this.$refs.metaDataTablet].filter(Boolean);
        metaDataRefs.forEach((ref) => {
          if (ref && typeof ref.loadMatchingBand === "function") ref.loadMatchingBand();
        });
        this.handleStickyBarItems();
      });
    } else {
      /* ── Init matching band section ── */
      this.initMatchingBandSection();
    }
    /* A toi-et-moi second diamond only ever lived in sessionStorage — a
       cold-pasted URL carrying secondDiamondId re-fetches it so the second
       stone survives a fresh open, matching the matchingBandId pattern. */
    const secondDiamondId = this.$route.query.secondDiamondId;
    if (secondDiamondId && !sessionStorage.getItem("selectedSecondDiamond")) {
      this.loadSecondDiamondFromUrl(secondDiamondId, this.$route.query.secondDiamondType).finally(() => {
        const secondDiamondRefs = [this.$refs.metaData, this.$refs.metaDataTablet, this.$refs.detailTable].filter(Boolean);
        secondDiamondRefs.forEach((ref) => {
          if (ref && typeof ref.loadSecondDiamond === "function") ref.loadSecondDiamond();
        });
        if (this.setting?.data && this.diamond?.data) {
          this.handleMediaGallery(this.setting.data.image_gallery, this.diamond.data.diamondImage);
        }
        this.handleStepData();
        this.handleStickyBarItems();
      });
    }
    try {
      var matchingBandFromSession = JSON.parse(sessionStorage.getItem("selectedMatchingBand") || "null");
      if (matchingBandFromSession?.id && !this.$route.query.matchingBandId) {
        this.$router.replace({
          query: { ...this.$route.query, matchingBandId: String(matchingBandFromSession.id) },
        });
      }
    } catch (e) {
      /* ignore */
    }
    try {
      var secondDiamondFromSession = JSON.parse(sessionStorage.getItem("selectedSecondDiamond") || "null");
      if (secondDiamondFromSession?.diamondId && !this.$route.query.secondDiamondId) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            secondDiamondId: String(secondDiamondFromSession.diamondId),
            secondDiamondType: secondDiamondFromSession.diamondType || "",
          },
        });
      }
    } catch (e) {
      /* ignore */
    }
    /* ── Re-check diamond availability on load (covers all mounted branches) ── */
    this.verifyDiamondsOnLoad();
    /* ── Diamond removed via the header "Remove" link: update in place, no reload ── */
    this._onDiamondRemoved = (e) => {
      var freshStepData = safeParseJSON("stepData");
      if (freshStepData) this.stepData = freshStepData;
      this.pageStickyItems = safeParseJSON("stickyBarItems", []);
      this.removedDiamondType = (e.detail && e.detail.diamondType) || "";
      this.showDiamondRemovedPopup = true;
    };
    window.addEventListener("roa:diamond-removed", this._onDiamondRemoved);
  },
  beforeUnmount() {
    handlePageBodyClass("remove", "pc-ringbuilder-final-ring-detail-page");
    window.removeEventListener("resize", this.handleViewportChange);
    window.removeEventListener("roa:diamond-removed", this._onDiamondRemoved);
  },
  computed: {
    // The live "360° Setting" hero's selection, handed to FinalRingMediaGallery
    // as a prop — that child owns the iframe and posts this into it (the src is
    // render-only, so the ring can't be read back out of it).
    //
    // DERIVED, not assigned: handleMediaGallery() runs whenever the gallery is
    // rebuilt, which can be before the setting fetch resolves — there is no sku
    // yet, so it produced null and, being a plain data field, stayed null for
    // the rest of the page's life even once the setting arrived. The embed then
    // never received a selection and rendered its own dashboard default ring.
    // A computed re-evaluates the moment its inputs land, which is what the
    // working React implementation does (pc-shopify Ring3DPreview: a useMemo
    // over ring3d/carat/engraving, pushed on 'ready' AND on every change).
    frgHeroSelection() {
      var sku = this.setting && this.setting.data && this.setting.data.sku;
      if (!sku) return null;
      var pType = this.$route.query.prongType;
      if (!pType && this.setting.data.prongType) pType = this.setting.data.prongType;
      if (!pType) {
        var saved = sessionStorage.getItem("selectedSetting");
        if (saved) {
          try { pType = JSON.parse(saved).prongType || null; } catch (e) {}
        }
      }
      var built = buildRing3dEmbedUrl({
        sku: sku,
        shape: this.diamond && this.diamond.data && this.diamond.data.shape,
        carat: this.diamond && this.diamond.data && this.diamond.data.caratWeight,
        metal: this.setting.settingMetal,
        prongType: pType,
        gem: this.setting.data.ring3dGem,
      });
      return built ? built.selection : null;
    },
    getSettingSizes() {
      if (this.defaultSettingCountry == "US") {
        return [
          "3",
          "3.25",
          "3.5",
          "3.75",
          "4",
          "4.25",
          "4.5",
          "4.75",
          "5",
          "5.25",
          "5.5",
          "5.75",
          "6",
          "6.25",
          "6.5",
          "6.75",
          "7",
          "7.25",
          "7.5",
          "7.75",
          "8",
          "8.25",
          "8.5",
          "8.75",
          "9",
          "9.25",
          "9.5",
          "9.75",
          "10",
          "10.25",
          "10.5",
          "10.75",
          "11",
          "11.25",
          "11.5",
          "11.75",
          "12",
          "12.25",
          "12.5",
          "12.75",
          "13",
          "13.25",
          "13.5",
          "13.75",
          "14",
          "14.25",
          "14.5",
          "14.75",
          "15",
        ];
      } else {
        return [
          "E",
          "E 1/2",
          "F",
          "F 1/2",
          "G",
          "G 1/2",
          "H",
          "H 1/2",
          "I",
          "I 1/2",
          "J",
          "J 1/2",
          "K",
          "K 1/2",
          "L",
          "L 1/2",
          "M",
          "M 1/2",
          "N",
          "N 1/2",
          "O",
          "O 1/2",
          "P",
          "P 1/2",
          "Q",
          "Q 1/2",
          "R",
          "R 1/2",
          "S",
          "S 1/2",
          "T",
          "T 1/2",
          "U",
          "U 1/2",
          "V",
          "V 1/2",
          "W",
          "W 1/2",
          "X",
          "X 1/2",
          "Y",
          "Y 1/2",
          "Z",
          "Z 1/2",
        ];
      }
    },
    getBannerImgUrl() {
      return `${baseAssetsUrl}/got_question_banner.jpg`;
    },
    getDetailPageBannerUrl() {
      return `${baseAssetsUrl}/diamonds-banner.jpg`;
    },
  },
  methods: {
    handleViewportChange() {
      this.isDesktopView = window.innerWidth >= 992;
    },

    /* ══════════════════════════════════════════
       MATCHING BAND METHODS
    ══════════════════════════════════════════ */

    initMatchingBandSection() {
      /* Hide if band already added to ring */
      if (sessionStorage.getItem("selectedMatchingBand")) {
        this.showMatchingBandSection = false;
        return;
      }

      // Remove matching band image from gallery when band is removed
      this.pageMedia = this.pageMedia.filter(function (item) {
        return item.name !== "Matching Band";
      });

      var rawSetting = sessionStorage.getItem("selectedSetting");
      var rawOptions = sessionStorage.getItem("selectedSettingOptions");

      if (!rawSetting || !rawOptions) {
        this.showMatchingBandSection = false;
        return;
      }

      var setting = JSON.parse(rawSetting);
      var options = JSON.parse(rawOptions);
      var band = setting && setting.availbleMatchingBandData;

      if (!band || !band.variants || band.variants.length === 0) {
        this.showMatchingBandSection = false;
        return;
      }

      /* Find variant matching current metal */
      var metal = (options.settingMetal || "").trim().toLowerCase();

      var matched =
        band.variants.find(function (v) {
          return v && v.title && v.title.trim().toLowerCase() === metal;
        }) || band.variants[0];

      this.matchingBandData = band;
      this.matchingBandVariant = matched;
      this.showMatchingBandSection = true;
    },

    getMatchingBandImage() {
      if (!this.matchingBandVariant) return "";
      var v = this.matchingBandVariant;
      return (
        (v.featured_image && (v.featured_image.src || v.featured_image)) ||
        (this.matchingBandData && this.matchingBandData.featured_image) ||
        ""
      );
    },

    /* Shared by addMatchingBandToRing() (live add) and the cold-open
       fetchMatchingBandData() path in mounted() — inserts/replaces the
       "Matching Band" gallery entry so both flows show the same thumbnail. */
    addMatchingBandImageToGallery(bandImg) {
      this.pageMedia = this.pageMedia.filter(function (item) {
        return item.name !== "Matching Band";
      });
      if (!bandImg) return;
      var bandItem = {
        full: bandImg,
        thumbnail: bandImg,
        popupUrl: bandImg,
        name: "Matching Band",
        type: "image",
        badge: "Matching Band",
      };
      // Insert after setting image (find last setting-type item)
      var insertIdx = -1;
      for (var i = 0; i < this.pageMedia.length; i++) {
        if (this.pageMedia[i].name === 'setting' || this.pageMedia[i].name === '360° Setting' || this.pageMedia[i].name === 'Setting Video') {
          insertIdx = i;
        }
      }
      if (insertIdx >= 0) {
        this.pageMedia.splice(insertIdx + 1, 0, bandItem);
      } else {
        this.pageMedia.push(bandItem);
      }
    },

    getMatchingBandOriginalPrice() {
      if (!this.matchingBandVariant) return "0.00";
      return ((this.matchingBandVariant.price || 0) / 100).toFixed(2);
    },

    getMatchingBandDiscountPrice() {
      if (!this.matchingBandVariant) return "0.00";
      return (((this.matchingBandVariant.price || 0) / 100) * 0.90).toFixed(2);
    },

    addMatchingBandToRing() {
      if (!this.matchingBandVariant || !this.matchingBandData) return;

      this.matchingBandLoading = true;

      sessionStorage.setItem(
        "selectedMatchingBand",
        JSON.stringify({
          id: String(this.matchingBandVariant.id),
          title: this.matchingBandData.title || "",
          price: String((this.matchingBandVariant.price || 0) / 100),
          image: this.getMatchingBandImage(),
        })
      );

      // Reflect the added band in the URL immediately (replace, not push, so
      // it doesn't add a new "back" stop) — this is what makes a copied link
      // or refresh right after adding the band still show it.
      this.$router.replace({
        query: { ...this.$route.query, matchingBandId: String(this.matchingBandVariant.id) },
      });

      // Add matching band image to gallery (after setting image)
      this.addMatchingBandImageToGallery(this.getMatchingBandImage());

      setTimeout(() => {
        this.matchingBandLoading = false;
        this.showMatchingBandSection = false;
        const primaryRef = this.isDesktopView ? "metaData" : "metaDataTablet";
        const secondaryRef = this.isDesktopView ? "metaDataTablet" : "metaData";
        const callLoadMatchingBand = (refName) => {
          const ref = this.$refs[refName];
          if (ref && typeof ref.loadMatchingBand === "function") {
            ref.loadMatchingBand();
            return true;
          }
          return false;
        };

        if (!callLoadMatchingBand(primaryRef)) {
          callLoadMatchingBand(secondaryRef);
        }
        this.handleStickyBarItems();

        setTimeout(() => {
          const cards = document.querySelectorAll('.nrl-card--band');
          let target = null;
          cards.forEach(el => {
            const rect = el.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) target = el;
          });
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            target.classList.add('nrl-card--just-added');
            setTimeout(() => target.classList.remove('nrl-card--just-added'), 2200);
          }
        }, 250);
      }, 2000);
    },

    scrollToMatchingBand() {
      var el = document.getElementById('matching-band-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    handleSoldOutDiamond(stone) {
      if (window.ROADiamond) window.ROADiamond.clearBuilderSelection(stone.diamondId);
      this.soldOutDiamond = stone;
      this.showSoldOutPopup = true;
      this.loading = false;
    },
    async verifyDiamondsOnLoad() {
      try {
        if (!window.ROADiamond) return;
        const q = this.$route.query;
        const stones = [];
        if (q.diamondId) {
          // Prefer the stored diamond's own type (always a valid API type: white/lab/...);
          // fall back to the query param.
          let primaryType = q.diamondType;
          try {
            const sel = JSON.parse(sessionStorage.getItem("selectedDiamond") || "null");
            if (sel && String(sel.diamondId) === String(q.diamondId) && sel.diamondType) {
              primaryType = sel.diamondType;
            }
          } catch (e) {
            /* ignore */
          }
          if (primaryType) stones.push({ diamondId: q.diamondId, diamondType: primaryType });
        }
        try {
          const second = JSON.parse(
            sessionStorage.getItem("selectedSecondDiamond") || "null"
          );
          if (second && second.diamondId) stones.push(second);
        } catch (e) {
          /* ignore */
        }
        for (const stone of stones) {
          if ((await window.ROADiamond.checkAvailability(stone)) === "soldout") {
            this.handleSoldOutDiamond(stone);
            return;
          }
        }
      } finally {
        this.soldOutCheckPending = false;
        // Perform the redirect that getMetaData deferred while the check was pending.
        if (this.dataNotFound && !this.showSoldOutPopup && !this.settingDiamondFetchPending) {
          this.$router.push({ name: "chooseSetting", query: { pageType: "setting" } });
        }
      }
    },

    /* ══════════════════════════════════════════
       EXISTING METHODS (unchanged)
    ══════════════════════════════════════════ */
    getDiamondData(id, type) {
      return axios({
        url: api,
        method: "post",
        data: {
          query: `{
              ${diamondDetailApiFunc}(diamondId:"${id}",diamondType:"${type}") {
                  diamond{
                          diamondId
                          shape
                          caratWeight
                          color
                          clarity
                          cut
                          symmetry
                          polish
                          deptPerc
                          tablePerc
                          length
                          width
                          depth
                          ratio
                          girdleMin
                          girdleMax                       
                          culet                       
                          fancyColor
                          flourIntensity                       
                          certificateFile
                          finalPriceAud
                          lab                                              
                          ${graphQLCurrencyFields()}
                          certificateNumber
                          stockNumber
                          diamondImage 
                          diamondVideo                       
                          diamondType
                          currencyCode
                          currencySymbol
                          availability
                      }
                  }
              }`,
        },
      })
        .then((response) => {
          const node = response?.data?.data?.diamondById;
          const arr = (node && node.diamond) || [];
          if (
            !arr.length ||
            /sold|out of stock|unavailable/i.test(String(arr[0].availability || ""))
          ) {
            this.handleSoldOutDiamond({ diamondId: id, diamondType: type });
            return;
          }
          let diamond = arr[0];
          if (Object.keys(diamond).length) {
            diamond = getCurrencyWiseDiamond(diamond);
            sessionStorage.setItem("selectedDiamond", JSON.stringify(diamond));
            this.dataNotFound = false;
            var stepTitle = getDiamondTitle(diamond, this.$route.query.diamondType);
            var price = `${diamond.currencySymbol}${parseFloat(
              diamond.finalPrice
            ).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
            Object.keys(this.stepData).forEach((v) => {
              if (this.stepData[v]["headerTitle"] == "DIAMOND") {
                this.stepData[v]["title"] = stepTitle;
                this.stepData[v]["price"] = price;
                this.stepData[v]["isView"] = true;
                this.stepData[v]["isChange"] = true;
                this.stepData[v]["id"] = diamond.diamondId;
                this.stepData[v]["diamondType"] = this.$route.query.diamondType;
                this.stepData[v]["page"] = "diamond";
                this.stepData[v]["diamondShape"] = diamond.shape;
                this.stepData[v]["placeholder"] = diamond.shape;
                this.stepData[v]["imgUrl"] = diamond.diamondImage;
                this.stepData[v] = appendCurrenciesToSteps(
                  this.stepData[v],
                  diamond
                );
              }
              /* Ensure setting step has View/Remove if setting data exists */
              if (this.stepData[v]["headerTitle"] == "SETTING" && this.setting.data && this.setting.data.name) {
                var setting = this.setting.data;
                this.stepData[v]["title"] =
                  setting.name.slice(0, 32) + (setting.name.length > 33 ? "..." : "");
                this.stepData[v]["price"] = `${setting.currency}${parseFloat(
                  setting.price
                ).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
                this.stepData[v]["isView"] = true;
                this.stepData[v]["isChange"] = true;
                this.stepData[v]["id"] = setting.id;
                this.stepData[v]["handle"] = setting.handle || "";
                this.stepData[v]["page"] = "setting";
                this.stepData[v]["imgUrl"] = setting.main_image;
                this.stepData[v]["placeholder"] = setting.main_image;
                this.stepData[v] = appendCurrenciesToSteps(
                  this.stepData[v],
                  setting
                );
              }
            });
            sessionStorage.setItem("stepData", JSON.stringify(this.stepData));
            this.handleDiamond(diamond);
          }
        })
        .catch((err) => {
          this.dataNotFound = true;
        });
    },
    /* A toi-et-moi second diamond only ever lived in sessionStorage — a
       cold-pasted URL carrying secondDiamondId re-fetches it so the second
       stone survives a fresh open, matching the matchingBandId pattern. */
    loadSecondDiamondFromUrl(id, type) {
      return axios({
        url: api,
        method: "post",
        data: {
          query: `{
              ${diamondDetailApiFunc}(diamondId:"${id}",diamondType:"${type}") {
                  diamond{
                          diamondId
                          shape
                          caratWeight
                          color
                          clarity
                          cut
                          symmetry
                          polish
                          deptPerc
                          tablePerc
                          length
                          width
                          depth
                          ratio
                          girdleMin
                          girdleMax
                          culet
                          fancyColor
                          flourIntensity
                          certificateFile
                          finalPriceAud
                          lab
                          ${graphQLCurrencyFields()}
                          certificateNumber
                          stockNumber
                          diamondImage
                          diamondVideo
                          diamondType
                          currencyCode
                          currencySymbol
                          availability
                      }
                  }
              }`,
        },
      })
        .then((response) => {
          const node = response?.data?.data?.diamondById;
          const arr = (node && node.diamond) || [];
          if (
            !arr.length ||
            /sold|out of stock|unavailable/i.test(String(arr[0].availability || ""))
          ) {
            this.handleSoldOutDiamond({ diamondId: id, diamondType: type });
            return;
          }
          let diamond = arr[0];
          if (Object.keys(diamond).length) {
            diamond = getCurrencyWiseDiamond(diamond);
            sessionStorage.setItem("selectedSecondDiamond", JSON.stringify(diamond));
          }
        })
        .catch(() => {
          /* ignore — second diamond simply won't be restored */
        });
    },
    getSettingData(id, opts) {
      // opts.keepCurrentOnFailure: a background variant swap (switchSettingVariant)
      // must leave the working page alone if its fetch fails, not turn a ring the
      // shopper is looking at into "No Ring Found".
      var keepCurrentOnFailure = !!(opts && opts.keepCurrentOnFailure);
      if (!id) {
        this.dataNotFound = true;
        this.loading = false;
        this.handleBreadcrumbs();
        this.handleMetaData();
        return Promise.resolve();
      }
      return axios
        .post(
          "/api/2026-04/graphql",
          {
            query: `
              query getVariant($id: ID!) {
                node(id: $id) {
                  ... on ProductVariant {
                    id
                    title
                    sku
                    price { amount currencyCode }
                    image { url altText }
                    selectedOptions { name value }
                    product {
                      id
                      title
                      handle
                      media(first: 10) {
                        nodes {
                          mediaContentType
                          ... on MediaImage {
                            image { url altText }
                          }
                          ... on Video {
                            sources { url }
                          }
                        }
                      }
                      productVideosMetafield: metafield(namespace: "custom", key: "product_videos") {
                        value
                      }
                      ring3dGemMetafield: metafield(namespace: "custom", key: "ring_3d_gem") {
                        value
                      }
                    }
                  }
                }
              }
            `,
            variables: { id: `gid://shopify/ProductVariant/${id}` },
          },
          { headers: { "Content-Type": "application/json" } }
        )
        .then((response) => {
          const v = response?.data?.data?.node;
          if (!v || !v.product) {
            if (keepCurrentOnFailure) return;
            this.dataNotFound = true;
            this.loading = false;
            this.handleBreadcrumbs();
            this.handleMetaData();
            return;
          }
          // A data=<hash> link only carries a trimmed setting (see
          // trimSettingForUrl in ring-builder-common-components.js) — no
          // images/media, to keep the URL under request-size limits. Merge
          // the freshly-fetched images/price on top of whatever is already
          // cached (attributes, tags, matching_band, variations) rather than
          // replacing it, so those decode-only fields survive this refetch.
          const previouslyCached = safeParseJSON("selectedSetting");
          const setting = { ...previouslyCached, ...this.buildSettingFromVariant(v) };
          sessionStorage.setItem("selectedSetting", JSON.stringify(setting));
          if (setting.settingVideo) {
            sessionStorage.setItem("selectedSettingVideo", setting.settingVideo);
          }
          this.dataNotFound = false;
          // Only the "last piece to arrive" should drop the loading skeleton —
          // handleSetting() itself only runs the media/breadcrumb/step cascade
          // once this.getDiamond is already true (its own mutual-flag gate), so
          // mirror that same condition here rather than always clearing
          // loading, which would reveal a setting-only page while a concurrent
          // getDiamondData() fetch (the fully-cold Promise.allSettled path) is
          // still in flight.
          if (this.getDiamond) {
            this.loading = false;
          }
          this.handleSetting(setting);
          // Whether a matching band is offered for this setting isn't derivable
          // from the variant/product fetch above — the storefront finds it by
          // scanning the womens-wedding-bands collection for a SKU-prefix match
          // (see snippets/custome-matching-wedding-band.liquid). Do the same
          // scan here so a cold-pasted URL still shows the "Matching Band
          // Available" prompt when one exists, not just when one was already added.
          if (setting.sku) {
            this.fetchMatchingBandForSku(setting.sku).then((band) => {
              if (!band) return;
              const refreshedSetting = JSON.parse(sessionStorage.getItem("selectedSetting") || "null");
              if (!refreshedSetting) return;
              refreshedSetting.availbleMatchingBandData = band;
              sessionStorage.setItem("selectedSetting", JSON.stringify(refreshedSetting));
              this.setting.data = refreshedSetting;
              this.initMatchingBandSection();
            });
          }
        })
        .catch((err) => {
          if (keepCurrentOnFailure) return;
          this.dataNotFound = true;
          this.loading = false;
          this.handleBreadcrumbs();
          this.handleMetaData();
        });
    },
    /* Scans the womens-wedding-bands collection for a product whose variant SKU
       shares this setting's SKU prefix (before the first "-"), mirroring the
       Liquid logic in custome-matching-wedding-band.liquid. Paginates because
       the match could be anywhere in the collection. Returns a product-shaped
       object (title + variants[]) matching what initMatchingBandSection() reads
       from setting.availbleMatchingBandData, or null if no match / on error. */
    async fetchMatchingBandForSku(settingSku) {
      const skuPrefix = String(settingSku).split("-")[0];
      if (!skuPrefix) return null;
      let cursor = null;
      const MAX_PAGES = 10; // hard cap so a collection with no match can't loop forever
      try {
        for (let page = 0; page < MAX_PAGES; page++) {
          const response = await axios.post(
            "/api/2026-04/graphql",
            {
              query: `
                query getWeddingBands($cursor: String) {
                  collection(handle: "womens-wedding-bands") {
                    products(first: 50, after: $cursor) {
                      pageInfo { hasNextPage endCursor }
                      nodes {
                        title
                        variants(first: 20) {
                          nodes {
                            id
                            sku
                            title
                            price { amount }
                            image { url }
                          }
                        }
                      }
                    }
                  }
                }
              `,
              variables: { cursor },
            },
            { headers: { "Content-Type": "application/json" } }
          );
          const productsPage = response?.data?.data?.collection?.products;
          if (!productsPage) return null;
          const matchedProduct = productsPage.nodes.find((product) =>
            product.variants.nodes.some((v) => String(v.sku).split("-")[0] === skuPrefix)
          );
          if (matchedProduct) {
            return {
              title: matchedProduct.title,
              variants: matchedProduct.variants.nodes.map((v) => ({
                id: Number(String(v.id).split("/").pop()),
                title: v.title,
                price: Math.round(Number(v.price.amount) * 100),
                featured_image: v.image?.url ? { src: v.image.url } : null,
              })),
            };
          }
          if (!productsPage.pageInfo.hasNextPage) return null;
          cursor = productsPage.pageInfo.endCursor;
        }
      } catch (e) {
        return null;
      }
      return null;
    },
    buildSettingFromVariant(v) {
      const mediaNodes = v.product.media?.nodes || [];
      const firstImage = mediaNodes.find((m) => m.mediaContentType === "IMAGE" && m.image?.url);
      const firstVideo = mediaNodes.find((m) => m.mediaContentType === "VIDEO" && m.sources?.[0]?.url);
      // The SELECTED variant's own photo wins. product.media[0] is whatever
      // Shopify lists first for the product — the default variant (e.g. 9K
      // White) — so preferring it showed a white-gold ring to a shopper who
      // picked yellow. It stays as the fallback for a variant with no image.
      const galleryImageUrl = v.image?.url || firstImage?.image?.url || null;

      // Mirrors getVideoKey() (assets/custom-video-player.js) / getCurrentSettingVideoUrl()
      // (assets/ring-builder-setting-detail.js): option1's metal word + option2 (diamond
      // shape), lowercased, "platinum" normalised to "white". Needed on a cold load (no
      // sessionStorage, no route query) since this setting's video lives in the
      // custom.product_videos metafield, not as Shopify native product media.
      const metafieldVideoUrl = (() => {
        try {
          var map = JSON.parse(v.product?.productVideosMetafield?.value || "{}");
          var option1 = v.selectedOptions?.[0]?.value || "";
          var option2 = v.selectedOptions?.[1]?.value || "";
          var metalParts = option1.trim().split(" ").filter(Boolean);
          var metalWord = metalParts.length > 1 ? metalParts[1].toLowerCase() : (metalParts[0] || "").toLowerCase();
          if (metalWord === "platinum") metalWord = "white";
          var shape = option2.trim().toLowerCase();
          var candidates = [];
          if (metalWord && shape) candidates.push(metalWord + shape);
          if (metalWord) candidates.push(metalWord);
          if (shape) candidates.push(shape);
          for (var i = 0; i < candidates.length; i++) {
            if (Object.prototype.hasOwnProperty.call(map, candidates[i])) return map[candidates[i]];
          }
          return null;
        } catch (e) {
          return null;
        }
      })();
      const routeSettingVideo = this.$route.query.settingVideo || sessionStorage.getItem("selectedSettingVideo") || metafieldVideoUrl || firstVideo?.sources?.[0]?.url || null;
      const setting = {
        id: Number(String(v.id).split("/").pop()),
        name: `${v.product.title} - ${v.title}`,
        sku: v.sku,
        handle: v.product.handle,
        option1: v.selectedOptions?.[0]?.value ?? null,
        option2: v.selectedOptions?.[1]?.value ?? null,
        // Same field the product page's "Choose This Setting" button writes, so a
        // swapped variant's shape reads the same way everywhere (see
        // syncSettingVariantToDiamondShape); otherwise the cached "Round" would
        // survive the merge in getSettingData() next to a fresh option2 "Cushion".
        selectedDiamondShape: v.selectedOptions?.[1]?.value ?? null,
        main_image: v.image?.url ?? null,
        featured_image: {
          src: v.image?.url ?? null,
          alt: v.image?.altText ?? null,
        },
        image_gallery: galleryImageUrl
          ? { full: galleryImageUrl, thumbnail: galleryImageUrl }
          : null,
        settingVideo: routeSettingVideo,
        // Not derivable from the Storefront API (they're custom UI choices, not
        // product/variant fields) — only recoverable from the route query (see
        // ring-builder.js's finalRingPage beforeEnter guard) or an already-cached
        // setting, same pattern as settingVideo above.
        bandWidth: this.$route.query.bandWidth || safeParseJSON("selectedSetting")?.bandWidth || null,
        prongType: this.$route.query.prongType || safeParseJSON("selectedSetting")?.prongType || null,
        engravingText: this.$route.query.engravingText || safeParseJSON("selectedSetting")?.engravingText || null,
        // Same per-product override product-3d-preview.liquid reads (assign gem =
        // product.metafields.custom.ring_3d_gem) — fetched live here so the "360°
        // Setting" hero and the cart-line preview render the merchant's configured
        // gem instead of a hardcoded default. Null when unset; buildRing3dEmbedUrl/
        // sealRing3dUrl never invent a fallback, same contract as the PDP.
        ring3dGem: v.product?.ring3dGemMetafield?.value || null,
      };
      // getCurrencyWiseSetting reads `finalPrice${currencyCode}` (raw, e.g. "AUD"),
      // unlike getCurrencyWiseDiamond which camel-cases it (e.g. "Aud") — set both
      // so the price resolves regardless of which casing convention is in play.
      const priceAmount = v.price ? Math.round(Number(v.price.amount)) : null;
      setting[`finalPrice${currencyCode}`] = priceAmount;
      setting[`finalPrice${toCamelCase(currencyCode)}`] = priceAmount;
      return getCurrencyWiseSetting(setting);
    },
    fetchMatchingBandData(id) {
      return axios
        .post(
          "/api/2026-04/graphql",
          {
            query: `
              query getVariant($id: ID!) {
                node(id: $id) {
                  ... on ProductVariant {
                    id
                    price { amount }
                    image { url }
                    product { title }
                  }
                }
              }
            `,
            variables: { id: `gid://shopify/ProductVariant/${id}` },
          },
          { headers: { "Content-Type": "application/json" } }
        )
        .then((response) => {
          const v = response?.data?.data?.node;
          if (!v || !v.product) return;
          sessionStorage.setItem(
            "selectedMatchingBand",
            JSON.stringify({
              id: String(id),
              title: v.product.title || "",
              price: String(v.price ? Number(v.price.amount) : 0),
              image: v.image?.url || "",
            })
          );
        })
        .catch(() => {
          /* leave selectedMatchingBand unset — band simply won't render */
        });
    },
    handleMetaList(metalList) {
      this.settingMetalList = [];
      if (metalList.length) {
        metalList.sort(function (a, b) {
          if (a > b) return 1;
          if (a < b) return -1;
          return 0;
        });
        metalList.map((metalLabel) => {
          this.settingMetalList.push(metalLabel.trim());
        });
      }
    },
    updateMetalList(metalName) {
      sessionStorage.setItem("finalRingSettingSize", this.setting.settingSize);
      this.changeInMetalLoading = true;
      this.setting.settingMetal = metalName;
      this.setting.settingSize = this.setting.settingSize;
      this.finalSettingSize = this.setting.settingSize;
      // this.loading=true
      if (sessionStorage.getItem("selectedSettingOptions")) {
        var settingOptions = JSON.parse(
          sessionStorage.getItem("selectedSettingOptions")
        );
        settingOptions.settingMetal = metalName;
        settingOptions["settingSize"] = this.setting.settingSize;
        sessionStorage.setItem(
          "selectedSettingOptions",
          JSON.stringify(settingOptions)
        );
      }

      var setting =
        this.setting.data.variations[
        `${metalName
          .toLowerCase()
          .replaceAll(" ", "-")}_${this.diamond.data.shape.toLowerCase()}`
        ];
      var settingId = setting.variation_id;
      this.loadOnlySetting(settingId, "setSessionStorage");
    },
    functionOnConnect() {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(["openForm", "Xyi5kc"]);
    },
    openDetail(e) {
      let element;
      if (e.target.nodeName == "svg") {
        element = e.target.parentElement;
        e.target.parentElement.classList.toggle("active");
      } else {
        element = e.target;
        e.target.classList.toggle("active");
      }

      var panel = element.nextElementSibling;
      if (
        Array.from(element.parentElement.classList).includes("accordion-active")
      ) {
        panel.style.maxHeight = null;
        // this.currentDetailTab = '';
      } else {
        panel.style.maxHeight = panel.scrollHeight + "px";
        // this.currentDetailTab = type;
      }
      element.parentElement.classList.toggle("accordion-active");
      element.parentElement.classList.toggle("accordion-non-active");
    },
    loadOnlySetting(settingId, method = undefined) {
      return this.getSettingData(settingId);
    },
    startWith(pageType) {
      if (pageType == "setting") {
        this.$router.push({
          name: "chooseSetting",
          query: { pageType: "setting" },
        });
      } else {
        this.$router.push({
          name: "chooseDiamonds",
          query: { pageType: "diamond" },
        });
      }
    },
    handleStepData(createStep = 0) {
      // Always rebuild the header step-bar from the live setting/diamond data
      // (this.setting.data / this.diamond.data — guaranteed populated by every caller of
      // this method) rather than trusting whatever "stepData" blob happens to be cached in
      // sessionStorage. That cache can go stale or be partially altered by unrelated flows
      // (e.g. a sold-out clear for a previous diamond) and end up out of sync with what's
      // actually loaded on this page, showing a step as blank even though it's selected.
      this.setInitialTwoSteps();
      if (createStep || !sessionStorage.getItem("redirection")) {
        this.setThirdStep();
      }
      this.stepData["thirdStep"]["headerTitle"] = "Ring";
      sessionStorage.setItem("stepData", JSON.stringify(this.stepData));
    },
    getConvertedStepPrice(step) {
      if (step.hasOwnProperty("price")) {
        step = getCurrencyWiseStep(step);
      }
      return step;
    },
    setInitialTwoSteps() {
      var firstPage = "settingPage";
      if (sessionStorage.getItem("firstPage")) {
        firstPage = sessionStorage.getItem("firstPage");
      }
      if (firstPage == "diamondPage") {
        /* set first step for diamond*/
        var diamond = this.diamond.data;
        this.stepData.firstStep.headerTitle = "DIAMOND";
        var stepTitle = getDiamondTitle(diamond);
        this.stepData.firstStep["price"] = `${diamond.currencySymbol
          }${parseFloat(diamond.finalPrice).toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })}`;
        this.stepData.firstStep["title"] =
          stepTitle.slice(0, 32) + (stepTitle.length > 33 ? "..." : "");
        this.stepData.firstStep["isView"] = true;
        this.stepData.firstStep["isChange"] = true;
        this.stepData.firstStep["id"] = diamond.diamondId;
        this.stepData.firstStep["diamondType"] = diamond.diamondType;
        this.stepData.firstStep["page"] = "diamond";
        this.stepData.firstStep["diamondShape"] = diamond.shape;
        this.stepData.firstStep["placeholder"] = diamond.shape;
        this.stepData.firstStep["imgUrl"] = diamond.diamondImage;
        this.stepData.firstStep = appendCurrenciesToSteps(
          this.stepData.firstStep,
          diamond
        );

        /* set second step for setting */
        var setting = this.setting.data;
        this.stepData.secondStep.headerTitle = "SETTING";
        this.stepData.secondStep["title"] =
          setting.name.slice(0, 32) + (setting.name.length > 33 ? "..." : "");
        this.stepData.secondStep["price"] = `${setting.currency}${parseFloat(
          setting.price
        ).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
        this.stepData.secondStep["isView"] = true;
        this.stepData.secondStep["isChange"] = true;
        this.stepData.secondStep["id"] = setting.id;
        this.stepData.secondStep["handle"] = setting.handle || "";
        this.stepData.secondStep["page"] = "setting";
        this.stepData.secondStep["imgUrl"] = setting.main_image;
        this.stepData.secondStep["placeholder"] = setting.main_image;
        this.stepData.secondStep = appendCurrenciesToSteps(
          this.stepData.secondStep,
          setting
        );
      } else {
        /* set second step for diamond*/
        var diamond = this.diamond.data;
        this.stepData.secondStep.headerTitle = "DIAMOND";
        var stepTitle = getDiamondTitle(diamond);
        this.stepData.secondStep["price"] = `${diamond.currencySymbol
          }${parseFloat(diamond.finalPrice).toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })}`;
        this.stepData.secondStep["title"] =
          stepTitle.slice(0, 32) + (stepTitle.length > 33 ? "..." : "");
        this.stepData.secondStep["isView"] = true;
        this.stepData.secondStep["isChange"] = true;
        this.stepData.secondStep["id"] = diamond.diamondId;
        this.stepData.secondStep["diamondType"] = diamond.diamondType;
        this.stepData.secondStep["page"] = "diamond";
        this.stepData.secondStep["diamondShape"] = diamond.shape;
        this.stepData.secondStep["placeholder"] = diamond.shape;
        this.stepData.secondStep["imgUrl"] = diamond.diamondImage;
        this.stepData.secondStep = appendCurrenciesToSteps(
          this.stepData.secondStep,
          diamond
        );

        /* set first step for setting */
        var setting = this.setting.data;
        this.stepData.firstStep.headerTitle = "SETTING";
        this.stepData.firstStep["title"] =
          setting.name.slice(0, 32) + (setting.name.length > 33 ? "..." : "");
        this.stepData.firstStep["price"] = `${setting.currency}${parseFloat(
          setting.price
        ).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
        this.stepData.firstStep["isView"] = true;
        this.stepData.firstStep["isChange"] = true;
        this.stepData.firstStep["id"] = setting.id;
        this.stepData.firstStep["handle"] = setting.handle || "";
        this.stepData.firstStep["page"] = "setting";
        this.stepData.firstStep["imgUrl"] = setting.main_image;
        this.stepData.firstStep["placeholder"] = setting.main_image;
        this.stepData.firstStep = appendCurrenciesToSteps(
          this.stepData.firstStep,
          setting
        );
      }
    },
    setThirdStep(anyMethod = undefined) {
      var stepTitle = `${this.setting.data.name} With ${getDiamondTitle(
        this.diamond.data
      )}`;
      this.stepData.thirdStep["title"] =
        stepTitle.slice(0, 32) + (stepTitle.length > 33 ? "..." : "");
      this.stepData.thirdStep["price"] = `${this.diamond.data.currencySymbol}${(
        parseFloat(this.diamond.data.finalPrice) +
        parseFloat(this.setting.data.price)
      ).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
      this.stepData.thirdStep["isReset"] = true;
      this.stepData.thirdStep["isView"] = true;
      this.stepData.thirdStep["id"] = "";
      this.stepData.thirdStep["page"] = "ring";
      this.stepData.thirdStep["diamondId"] = this.$route.query.diamondId;
      this.stepData.thirdStep["diamondType"] = this.$route.query.diamondType;
      this.stepData.thirdStep["imgUrl"] = this.setting.data.main_image;
      this.stepData.thirdStep = appendCurrenciesToFinalSteps(
        this.stepData.thirdStep,
        this.diamond.data,
        this.setting.data
      );
      if (!!anyMethod) {
        this.stepData["thirdStep"]["headerTitle"] = "Ring";
        this.stepData.thirdStep["settingId"] = this.setting.data.id;
        sessionStorage.setItem("stepData", JSON.stringify(this.stepData));
      } else {
        this.stepData.thirdStep["settingId"] = this.$route.query.settingId;
      }
    },
    handleStickyBarItems() {
      var diamond = this.diamond && this.diamond.data && Object.keys(this.diamond.data).length ? this.diamond.data : null;
      var setting = this.setting && this.setting.data && Object.keys(this.setting.data).length ? this.setting.data : null;

      /* Build fresh sticky items from live data when both exist */
      if (diamond && setting) {
        this.pageStickyItems = [];
        var firstPage = sessionStorage.getItem("firstPage") || "settingPage";

        /* diamond data */
        var stepTitle = getDiamondTitle(diamond);
        var dRawPrice = Math.round(parseFloat(diamond.finalPrice));
        var dIsLab = (diamond.diamondType === "lab" || diamond.diamondType === "lab_color") && dRawPrice >= 500;
        var dDiscountedPrice = dIsLab ? Math.round(dRawPrice * 0.70) : dRawPrice;
        var diamondData = {
          title: stepTitle.slice(0, 32) + (stepTitle.length > 33 ? "..." : ""),
          page: "diamond",
          imgUrl: diamond.diamondImage,
          placeholder: diamond.shape,
          price: `${diamond.currencySymbol}${dDiscountedPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
          originalPrice: dIsLab ? `${diamond.currencySymbol}${dRawPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "",
          discountLabel: dIsLab ? "30% Off" : "",
          id: diamond.diamondId,
          diamondType: diamond.diamondType,
          cut: diamond.cut || "",
          color: diamond.color || "",
          clarity: diamond.clarity || "",
          lab: diamond.lab || "",
        };
        diamondData = appendCurrenciesToStickyBarItems(diamondData, diamond);

        /* setting data */
        var settingData = {
          title:
            setting.name.slice(0, 32) + (setting.name.length > 33 ? "..." : ""),
          page: "setting",
          imgUrl: setting.main_image,
          placeholder: setting.main_image,
          price: `${setting.currency}${parseFloat(setting.price).toLocaleString(
            "en-US",
            { minimumFractionDigits: 2 }
          )}`,
          id: setting.id,
          handle: setting.handle || "",
          metal: this.setting.settingMetal || "",
          size: this.finalSettingSize && this.finalSettingSize !== "Select Ring Size" ? this.finalSettingSize : "",
        };
        settingData = appendCurrenciesToStickyBarItems(settingData, setting);

        if (firstPage == "diamondPage") {
          this.pageStickyItems.push(diamondData, settingData);
        } else {
          this.pageStickyItems.push(settingData, diamondData);
        }

        /* second diamond (Toi-Moi) — read from sessionStorage */
        var secondDiamondRaw = sessionStorage.getItem("selectedSecondDiamond");
        if (secondDiamondRaw) {
          try {
            var secondDiamond = JSON.parse(secondDiamondRaw);
            if (secondDiamond && secondDiamond.diamondId) {
              var secTitle = getDiamondTitle(secondDiamond);
              var secSymbol = secondDiamond.currencySymbol || diamond.currencySymbol;
              var secRawPrice = Math.round(parseFloat(secondDiamond.finalPrice));
              var secIsLab = (secondDiamond.diamondType === "lab" || secondDiamond.diamondType === "lab_color") && secRawPrice >= 500;
              var secDiscountedPrice = secIsLab ? Math.round(secRawPrice * 0.70) : secRawPrice;
              var secondDiamondData = {
                title: secTitle.slice(0, 32) + (secTitle.length > 33 ? "..." : ""),
                page: "diamond",
                imgUrl: secondDiamond.diamondImage,
                placeholder: secondDiamond.shape,
                price: `${secSymbol}${secDiscountedPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
                originalPrice: secIsLab ? `${secSymbol}${secRawPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "",
                discountLabel: secIsLab ? "30% Off" : "",
                id: secondDiamond.diamondId,
                diamondType: secondDiamond.diamondType,
                cut: secondDiamond.cut || "",
                color: secondDiamond.color || "",
                clarity: secondDiamond.clarity || "",
                lab: secondDiamond.lab || "",
                isSecondDiamond: true,
              };
              secondDiamondData = appendCurrenciesToStickyBarItems(secondDiamondData, secondDiamond);
              this.pageStickyItems.push(secondDiamondData);
            }
          } catch (e) { /* ignore parse errors */ }
        }

        /* matching band — read from sessionStorage */
        var matchingBandRaw = sessionStorage.getItem("selectedMatchingBand");
        if (matchingBandRaw) {
          try {
            var matchingBand = JSON.parse(matchingBandRaw);
            if (matchingBand && matchingBand.id) {
              var bandRawPrice = Math.round(parseFloat(String(matchingBand.price).replace(/,/g, "")));
              var bandDiscountedPrice = Math.round(bandRawPrice * 0.90);
              var currSymbol = setting.currency || diamond.currencySymbol || "$";
              this.pageStickyItems.push({
                title: matchingBand.title || "Matching Band",
                page: "matchingBand",
                imgUrl: matchingBand.image || "",
                placeholder: "",
                price: `${currSymbol}${bandDiscountedPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
                originalPrice: `${currSymbol}${bandRawPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
                discountLabel: "10% Off",
                id: matchingBand.id,
              });
            }
          } catch (e) { /* ignore parse errors */ }
        }

        sessionStorage.setItem(
          "stickyBarItems",
          JSON.stringify(this.pageStickyItems)
        );
      } else if (sessionStorage.getItem("stickyBarItems")) {
        /* fallback: read from session when live data not yet loaded */
        this.pageStickyItems = this.getConvertedStickyBarItems(
          JSON.parse(sessionStorage.getItem("stickyBarItems"))
        );
      }
    },
    getConvertedStickyBarItems(stickyBarItems) {
      if (stickyBarItems.length) {
        stickyBarItems.map((stickyBarItem) => {
          if (stickyBarItem.hasOwnProperty("price")) {
            stickyBarItem = getCurrencyWiseStickyItem(stickyBarItem);
          }
        });
      }
      return stickyBarItems;
    },
    setFinalRingSize(ringSize) {
      this.finalSettingSize = ringSize;
      if (sessionStorage.getItem("selectedSettingOptions")) {
        var settingOptions = JSON.parse(
          sessionStorage.getItem("selectedSettingOptions")
        );
        settingOptions.settingCountry = this.defaultSettingCountry;
        settingOptions.settingSize = ringSize;
        sessionStorage.setItem(
          "selectedSettingOptions",
          JSON.stringify(settingOptions)
        );
      }
    },
    /* initializeSettingSize() {
            this.setting.settingSize = 'Select Ring Size'
            if (sessionStorage.getItem("selectedSettingOptions")) {
                var settingOptions = JSON.parse(sessionStorage.getItem("selectedSettingOptions"))
                if (this.defaultSettingCountry == settingOptions.settingCountry) {
                    this.setting.settingSize = settingOptions.settingSize
                }
            }             
        }, */
    loadFinalRing(settingId, diamondId) {
      return Promise.allSettled([
        this.getDiamondData(diamondId, this.$route.query.diamondType),
        this.getSettingData(settingId),
      ]).then(() => this.reconcileDataNotFound());
    },
    /* getDiamondData/getSettingData run concurrently and each independently flips
       dataNotFound to reflect ITS OWN success/failure — whichever settles last wins,
       so a transient failure in one (e.g. a third-party diamond API request blocked
       by an ad/privacy blocker, or throwing on an unexpected response shape) can
       stomp a genuinely successful (or failed) result from the other. Recompute
       dataNotFound from the actual resulting state once both have settled, rather
       than trusting whichever flag write happened last — covers both directions:
       both actually succeeded but dataNotFound was left true, AND one actually
       failed but dataNotFound was left false by the other's unrelated success. */
    reconcileDataNotFound() {
      const hasDiamond = !!(this.diamond?.data && this.diamond.data.diamondId);
      const hasSetting = !!(this.setting?.data && this.setting.data.name);
      if (hasDiamond && hasSetting && this.dataNotFound) {
        this.dataNotFound = false;
        this.loading = false;
        this.handleFinalRingOptions();
        this.handleMediaGallery(this.setting.data.image_gallery, this.diamond.data.diamondImage);
        this.handleBreadcrumbs();
        this.handleMetaData();
        this.handleStepData();
        this.handleStickyBarItems();
      } else if ((!hasDiamond || !hasSetting) && !this.dataNotFound) {
        this.dataNotFound = true;
        this.loading = false;
        this.handleBreadcrumbs();
        this.handleMetaData();
      }
    },
    loadDiamond(diamondId) {
      if (
        !this.$options.rapnetRequestDiamonds.includes(
          (this.$route.query.diamondType || "").toLowerCase()
        )
      ) {
        return axios({
            url: api,
            method: "post",
            data: {
              query: `{
                            ${diamondDetailApiFunc}(diamondId:"${diamondId}",diamondType:"${this.$route.query.diamondType}") {
                                diamond{
                                       diamondId
                                       shape
                                       caratWeight
                                       color
                                       clarity
                                       cut
                                       symmetry
                                       polish
                                       deptPerc
                                       tablePerc
                                       length
                                       width
                                       depth
                                       ratio
                                       girdleMin
                                       girdleMax
                                       culet
                                       fancyColor
                                       flourIntensity
                                       certificateFile
                                       lab
                                       ${graphQLCurrencyFields()}
                                       finalPriceAud
                                       certificateNumber
                                       stockNumber
                                       diamondImage
                                       diamondVideo
                                       diamondType
                                       currencyCode
                                       currencySymbol
                                    }
                                }
                            }`,
            },
          })
            .then((response) => {
              var node = response.data.data[diamondDetailApiFunc];
              var arr = (node && node.diamond) || [];
              if (arr.length) {
                this.dataNotFound = false;
                this.loading = false;
                this.diamond.data = this.getConvertedDiamonds(arr[0]);
                this.handleMediaGallery(
                  this.setting.data.image_gallery,
                  this.diamond.data.diamondImage
                );
                this.handleBreadcrumbs();
                this.handleMetaData();
                this.setNewSettingData();
                this.setNewDiamondData();
                this.handleFinalRingOptions();
        
                this.handleStepData(1);
                this.handleStickyBarItems(1);
              } else {
                // Diamond is gone from the feed -> show sold-out popup, don't silently redirect.
                this.loading = false;
                this.handleSoldOutDiamond({
                  diamondId: diamondId,
                  diamondType: this.$route.query.diamondType,
                });
              }
            })
            .catch((err) => {
              this.dataNotFound = true;
              this.loading = false;
              this.handleBreadcrumbs();
              this.handleMetaData();
            });
      } else {
        this.dataNotFound = true;
        this.loading = false;
        this.handleBreadcrumbs();
        this.handleMetaData();
      }
    },
    getConvertedDiamonds(diamond) {
      diamond = getCurrencyWiseDiamond(diamond);
      return diamond;
    },
    setNewSettingData() {
      sessionStorage.setItem(
        "selectedSetting",
        JSON.stringify(this.setting.data)
      );
      if (this.setting?.data?.settingVideo) {
        sessionStorage.setItem("selectedSettingVideo", this.setting.data.settingVideo);
      } else {
        sessionStorage.removeItem("selectedSettingVideo");
      }
    },
    setNewDiamondData() {
      sessionStorage.setItem(
        "selectedDiamond",
        JSON.stringify(this.diamond.data)
      );
    },
    handleFinalRingOptions() {
      var isNewSetting = 1;
      var selectedSettingOptions = {};
      if (sessionStorage.getItem("selectedSettingOptions")) {
        var selectedSettingOptions = JSON.parse(
          sessionStorage.getItem("selectedSettingOptions")
        );
        if (selectedSettingOptions.settingId != this.$route.query.settingId) {
          isNewSetting = 1;
        } else {
          isNewSetting = 0;
        }
      }
      this.setting.settingMetal = selectedSettingOptions.settingMetal;
      if (isNewSetting) {
        // A URL opened fresh (QR scan, shared link, another device) carries the
        // user's ring size/metal choice as query params since they aren't stored
        // on the product itself — fall back to those when there's no matching
        // cached selectedSettingOptions for this settingId.
        const urlRingSize = this.$route.query.ringSize;
        const urlSettingMetal = this.$route.query.settingMetal;
        selectedSettingOptions["settingCountry"] = "US";
        selectedSettingOptions["settingId"] = this.$route.query.settingId;
        selectedSettingOptions["settingMetal"] = urlSettingMetal || selectedSettingOptions.settingMetal;
        selectedSettingOptions["settingSize"] = urlRingSize || selectedSettingOptions.settingSize;
        selectedSettingOptions["settingDiamondShape"] = this.getSettingDiamond(
          this.$route.query.settingId
        );
        this.setting.settingMetal = selectedSettingOptions["settingMetal"];
        this.setting.settingSize = selectedSettingOptions["settingSize"];
        this.finalSettingSize = selectedSettingOptions["settingSize"];
        sessionStorage.setItem(
          "selectedSettingOptions",
          JSON.stringify(selectedSettingOptions)
        );
      } else {
        this.setting.settingSize = selectedSettingOptions.settingSize;
        this.finalSettingSize = selectedSettingOptions.settingSize;
        this.defaultSettingCountry = selectedSettingOptions.settingCountry;
      }
    },
    getSettingDiamond(settingId) {
      var settingVariations = this.setting.data.variations;
      var settingDiamond = "";
      for (var settingSlug in settingVariations) {
        if (settingVariations[settingSlug].variation_id == settingId) {
          settingDiamond = settingVariations[settingSlug].diamond;
          break;
        }
      }
      return settingDiamond;
    },
    getSettingMetal(metalVariations, currentSettingId) {
      for (var metalSlug in metalVariations) {
        if (metalVariations[metalSlug].variation_id == currentSettingId) {
          return metalVariations[metalSlug].variation_name;
        }
      }
    },
    setNewImage(targetElement, diamondShape) {
      var stage = parseInt(targetElement.dataset.imgErrorStage || "0");
      targetElement.dataset.imgErrorStage = String(stage + 1);
      if (stage === 0) {
        if (targetElement.className == "down-sliders") {
          targetElement.parentElement.style.display = "none";
        }
        targetElement.setAttribute("src", getShapeFallbackImage(diamondShape));
      } else if (stage === 1) {
        targetElement.setAttribute("src", shapeToImageUrl.DEFAULT);
      }
    },
    goBack() {
      if (sessionStorage.getItem("firstPage")) {
        var firstPage = sessionStorage.getItem("firstPage");
        if (firstPage == "diamondPage") {
          var selectedDiamond = null;
          try {
            selectedDiamond = JSON.parse(sessionStorage.getItem("selectedDiamond") || "null");
          } catch (e) {
            /* ignore parse errors */
          }
          var diamondType =
            getDiamondListingType(selectedDiamond && selectedDiamond.diamondType) ||
            "natural-diamonds";
          this.$router.push({
            name: "chooseDiamonds",
            params: { diamondType },
            query: { pageType: "diamond" },
          });
        } else {
          this.$router.push({
            name: "chooseSetting",
            query: { pageType: "setting" },
          });
        }
        return;
      }
      this.$router.push({
        name: "chooseSetting",
        query: { pageType: "setting" },
      });
    },
    closeDiamondRemovedPopup() {
      this.showDiamondRemovedPopup = false;
    },
    findAnotherDiamondAfterRemove() {
      this.showDiamondRemovedPopup = false;
      // removedDiamondType here is already the URL collection slug (e.g. "lab-diamonds"),
      // per getDiamondListingType() in removeListing — not the API diamondType key.
      window.location.href = "/collections/" + (this.removedDiamondType || "natural-diamonds");
    },
    handleSetting(setting) {
      this.setting.data = setting;
      // Not every caller's `setting` carries settingVideo (e.g. the plain-product-page
      // "Choose This Setting" button — snippets/ring-builder-choose-setting-button.liquid —
      // stores the video separately under sessionStorage.selectedSettingVideo, never as a
      // field on the setting object itself), so don't treat its absence here as "this
      // setting has no video" and delete an already-correct cached value.
      if (setting?.settingVideo) {
        sessionStorage.setItem("selectedSettingVideo", setting.settingVideo);
      }
      this.getSetting = true;

      if (this.getDiamond) {
        sessionStorage.setItem(
          "firstPage",
          `${this.stepData.firstStep.headerTitle.toLowerCase()}Page`
        );
        if (this.redirectionPossible) {
          this.handleStepData();
          let URL = sessionStorage.getItem("redirection");
          window.location.href = baseUrl + `/${URL}`;
        } else {
          // delete setting.attributes["Setting_Config_Metal"];
          // this.setting.attributes = setting.attributes;
          this.setting.attributes = [];
          this.handleStepData();
          let selectedDiamond = JSON.parse(
            sessionStorage.getItem("selectedDiamond")
          );
          // this.handleMetaList(
          //   setting.attributes.Engagement_Ring_Metal_Type
          // );
          // console.log(setting.featured_image);
          // return;
          this.handleFinalRingOptions();
          this.handleMediaGallery(
            setting.image_gallery,
            selectedDiamond.diamondImage
          );
          this.handleBreadcrumbs();
          this.handleMetaData();
          this.handleStickyBarItems();
        }
      }
    },
    convertCachedSetting(setting) {
      setting = getCurrencyWiseSetting(setting);
      return setting;
    },
    handleSettingOptions(settingOptions) {
      this.setting.settingSize = settingOptions.settingSize;
      this.setting.settingMetal = settingOptions.settingMetal;
      this.defaultSettingCountry = settingOptions.settingCountry;
      this.finalSettingSize = settingOptions.settingSize;
    },
    handleDiamond(diamond) {
      this.diamond.data = this.convertCachedDiamond(diamond);
      this.getDiamond = true;
      if (this.getSetting && !this.redirectionPossible) {
        sessionStorage.setItem(
          "firstPage",
          `${this.stepData.firstStep.headerTitle.toLowerCase()}Page`
        );
        if (this.redirectionPossible) {
          this.handleStepData();
          let URL = sessionStorage.getItem("redirection");
          window.location.href = baseUrl + `/${URL}`;
        } else {
          let selectedSetting = JSON.parse(
            sessionStorage.getItem("selectedSetting")
          );
          this.handleMetaList(
            selectedSetting.attributes.Engagement_Ring_Metal_Type
          );
          this.handleFinalRingOptions();
          this.handleMediaGallery(
            selectedSetting.image_gallery,
            diamond.diamondImage
          );

          this.handleBreadcrumbs();
          this.handleMetaData();
          this.handleStepData();
          this.handleStickyBarItems();
        }
      }
    },
    convertCachedDiamond(diamond) {
      diamond = getCurrencyWiseDiamond(diamond);
      return diamond;
    },
    handleMediaGallery(settingImageGallery, diamondImage) {
      var media = [];
      var hasSecondDiamond = false;

      // Check for second diamond
      var storedSecond = sessionStorage.getItem("selectedSecondDiamond");
      var secondDiamondObj = null;
      if (storedSecond) {
        try {
          secondDiamondObj = JSON.parse(storedSecond);
          if (secondDiamondObj) hasSecondDiamond = true;
        } catch(e) {}
      }

      // 1) Setting tile — a LIVE 3D embed synced to the exact ring the shopper
      // configured (same band/shape/metal/prong the product page's own 3D viewer
      // showed), so this page previews the real selection instead of the
      // manufacturer's fixed rotation video. Falls back to that video when no
      // band (SKU prefix) can be resolved. The template already renders an
      // <iframe> for any "video"-type tile whose `full` isn't a .mp4/.webm, so
      // no markup change is needed here.
      // Address only — the ring itself travels over postMessage, from the
      // frgHeroSelection computed above.
      var settingSkuForVideo = this.setting && this.setting.data && this.setting.data.sku;
      var builtHero = settingSkuForVideo ? buildRing3dEmbedUrl({ sku: settingSkuForVideo }) : null;
      var ring3dEmbedUrl = builtHero ? builtHero.url : null;
      // Prefer the route query (always populated synchronously on this load) over
      // sessionStorage, which the mounted() seed writes to but isn't guaranteed to
      // have landed yet if handleMediaGallery runs first.
      var sVideo = ring3dEmbedUrl;
      if (!sVideo) {
        sVideo = this.$route.query.settingVideo || sessionStorage.getItem("selectedSettingVideo");
        if (!sVideo && this.setting && this.setting.data && this.setting.data.settingVideo) {
          sVideo = this.setting.data.settingVideo;
        }
        if (!sVideo) {
          var savedSetting = sessionStorage.getItem("selectedSetting");
          if (savedSetting) {
            try { sVideo = JSON.parse(savedSetting).settingVideo || null; } catch(e) {}
          }
        }
      }
      // 3D is off (no embed id): a stale embed url carried in the route query /
      // sessionStorage must not revive the viewer, so only real video files pass.
      if (!ring3dEmbedUrl && sVideo && String(sVideo).indexOf("/embed/") !== -1) sVideo = null;
      if (sVideo) {
        media.push({
          full: sVideo,
          thumbnail: null,
          name: ring3dEmbedUrl ? "360° Setting" : "Setting Video",
          type: "video",
        });
      }

      // 2) Setting image
      if (settingImageGallery) {
        var sImg = Object.assign({ type: 'image', name: 'setting' }, settingImageGallery);
        if (sImg.thumbnail && !/^https?:\/\//.test(sImg.thumbnail)) {
          sImg.thumbnail = sImg.full || null;
        }
        media.push(sImg);
      }

      // 3) Matching band image (if exists)
      var savedBand = sessionStorage.getItem("selectedMatchingBand");
      if (savedBand) {
        try {
          var bandData = JSON.parse(savedBand);
          if (bandData && bandData.image) {
            media.push({
              full: bandData.image,
              thumbnail: bandData.image,
              popupUrl: bandData.image,
              name: "Matching Band",
              type: "image",
              badge: "Matching Band",
            });
          }
        } catch(e) {}
      }

      // 4) Diamond video (first diamond)
      var dVideo = this.diamond && this.diamond.data && this.diamond.data.diamondVideo;
      if (dVideo) {
        media.push({
          full: dVideo,
          thumbnail: null,
          name: hasSecondDiamond ? "360° First Diamond" : "360° Diamond",
          type: "video",
          badge: hasSecondDiamond ? "First Diamond" : null,
        });
      }

      // 5) Diamond image (first diamond) – always show; fall back to shape placeholder if missing
      var firstDiamond = this.diamond && this.diamond.data;
      var dImgSrc = (diamondImage && diamondImage.toLowerCase() !== 'na') ? diamondImage : getShapeFallbackImage(firstDiamond && firstDiamond.shape);
      media.push({
        full: dImgSrc,
        thumbnail: dImgSrc,
        popupUrl: dImgSrc,
        name: hasSecondDiamond ? "First Diamond" : "diamond",
        type: "image",
        badge: hasSecondDiamond ? "First Diamond" : null,
        diamondShape: firstDiamond && firstDiamond.shape,
      });

      // 6) First diamond certificate
      if (firstDiamond && (firstDiamond.certificateNumber || firstDiamond.certificateFile)) {
        media.push({
          type: "certificate",
          name: hasSecondDiamond ? "First Diamond Certificate" : "Certificate",
          badge: hasSecondDiamond ? "First Diamond" : null,
          diamondData: firstDiamond,
        });
      }

      // 7) Second diamond video (if exists)
      if (hasSecondDiamond && secondDiamondObj.diamondVideo) {
        media.push({
          full: secondDiamondObj.diamondVideo,
          thumbnail: null,
          name: "360° Second Diamond",
          type: "video",
          badge: "Second Diamond",
        });
      }

      // 8) Second diamond image (if exists) – always show with shape fallback if missing
      if (hasSecondDiamond) {
        var sDImgSrc = (secondDiamondObj.diamondImage && secondDiamondObj.diamondImage.toLowerCase() !== 'na') ? secondDiamondObj.diamondImage : getShapeFallbackImage(secondDiamondObj.shape);
        media.push({
          full: sDImgSrc,
          thumbnail: sDImgSrc,
          popupUrl: sDImgSrc,
          name: "Second Diamond",
          type: "image",
          badge: "Second Diamond",
          diamondShape: secondDiamondObj.shape,
        });
      }

      // 9) Second diamond certificate (if exists)
      if (hasSecondDiamond && (secondDiamondObj.certificateNumber || secondDiamondObj.certificateFile)) {
        media.push({
          type: "certificate",
          name: "Second Diamond Certificate",
          badge: "Second Diamond",
          diamondData: secondDiamondObj,
        });
      }

      // ── Append banner images at the end of the gallery ──
      var assets = (typeof window !== "undefined" && window.__TRYON_ASSETS) || {};
      var topSellingUrl  = assets.topSellingBanner || (baseAssetsUrl + '/top-selling-rings-banner-img.webp');
      var igiBannerUrl   = assets.igiCertBanner    || (baseAssetsUrl + '/igi-certified-banner-img.webp');
      var discountUrl    = assets.discountBanner   || (baseAssetsUrl + '/70-discount-banner.png');

      var settingSku = this.setting && this.setting.data && this.setting.data.sku;
      var skuBase = settingSku ? String(settingSku).split('-')[0] : '';
      var isTopSelling = FINAL_RING_TOP_SELLING_SKUS.has(skuBase);

      if (isTopSelling) {
        media.push({
          full: topSellingUrl,
          thumbnail: topSellingUrl,
          name: "Top Selling Banner",
          type: "image",
          link: '/pages/book-a-video-call?product=' + encodeURIComponent(window.location.pathname + window.location.search),
        });
      }

      media.push({
        full: igiBannerUrl,
        thumbnail: igiBannerUrl,
        name: "IGI Certified Banner",
        type: "image",
      });

      media.push({
        full: discountUrl,
        thumbnail: discountUrl,
        name: "Discount Banner",
        type: "image",
      });
      // ──────────────────────────────────────────────────────────────

      this.pageMedia = media.filter(function (el) {
        return el !== null && el !== undefined;
      });
      this.syncSettingVariantToDiamondShape();
      // debug removed
    },
    /* Keeps the setting variant in step with the selected diamond's shape.
       Setting variants are metal × shape (option1 × option2): the ring is made
       with a head for one shape, and that variant is what the cart receives.
       The shopper may pair a variant with a diamond of another shape (product
       page shape-mismatch "Continue", or a diamond changed after the setting),
       and the 3D hero already renders the DIAMOND's shape — but the page, and
       the cart line, kept the clicked variant ("9K White / Round" with a
       Cushion stone). The legacy setNewSettingAsPerDiamond()
       (ring-builder-diamond-detail.js) meant to do this swap via a `variations`
       map only the old WooCommerce API supplied, which Shopify never provides,
       so it silently did nothing. Here: find the sibling variant (same product,
       same metal, the diamond's shape) and reload the page's setting as that
       variant — photos, name, price, SKU, the URL's settingId and the cart's
       variant id all follow. No-op when the shapes already agree, no sibling
       exists in that shape and metal, or a second stone is in play (toi-et-moi
       pairs have their own rules — the product page skips them too). */
    syncSettingVariantToDiamondShape() {
      var setting = this.setting && this.setting.data;
      var settingId = setting && setting.id;
      var wantShape = normalizeShapeKey(this.diamond && this.diamond.data && this.diamond.data.shape);
      var isTwoStone =
        !!sessionStorage.getItem("selectedSecondDiamond") ||
        String((setting && setting.tags) || "").toLowerCase().indexOf("toi-et-moi") !== -1;
      var options = safeParseJSON("selectedSettingOptions") || {};
      var settingShape =
        (setting && (setting.option2 || setting.selectedDiamondShape)) || options.settingDiamondShape || "";
      if (!settingId || !wantShape || isTwoStone || normalizeShapeKey(settingShape) === wantShape) {
        this._settingShapeSyncKey = null;
        return;
      }
      var metal = this.setting.settingMetal || (setting && setting.option1) || options.settingMetal || "";
      var key = [settingId, String(metal).trim().toLowerCase(), wantShape].join("|");
      if (this._settingShapeSyncKey === key) return; // resolved, or in flight, for this exact pairing
      this._settingShapeSyncKey = key;
      var self = this;
      this.getSettingSiblingVariants(settingId)
        .then(function (variants) {
          if (self._settingShapeSyncKey !== key) return; // setting/diamond changed meanwhile
          var sibling = pickSettingVariantForShape(variants, settingId, metal, wantShape);
          if (!sibling) return;
          return self.switchSettingVariant(sibling);
        })
        .catch(function () {
          if (self._settingShapeSyncKey === key) self._settingShapeSyncKey = null; // let a later rebuild retry
        });
    },
    /* Reloads this page's setting as another variant of the same product, down
       the same getSettingData() path a cold load takes — after pointing the
       cached options and the URL at it, so handleFinalRingOptions() keeps the
       shopper's size/metal (it treats a settingId that differs from the URL as
       a brand-new setting) and the step bar, breadcrumb and share links carry
       the new id. A failed reload leaves the current setting in place. */
    switchSettingVariant(variant) {
      var self = this;
      var options = safeParseJSON("selectedSettingOptions") || {};
      options.settingId = variant.id;
      if (variant.option1) options.settingMetal = variant.option1;
      if (variant.option2) options.settingDiamondShape = variant.option2;
      try { sessionStorage.setItem("selectedSettingOptions", JSON.stringify(options)); } catch (e) {}
      // The ring crumb is keyed by name/id — drop the old variant's so the
      // reload's handleBreadcrumbs() replaces it instead of adding a second.
      this.breadcrumbs = this.breadcrumbs.filter(function (crumb) { return !crumb.dedupKey; });
      var query = { ...this.$route.query, settingId: String(variant.id) };
      // The cached 360° video belongs to the old variant — drop it so
      // buildSettingFromVariant() looks the new one up from the product_videos
      // metafield (keyed by metal + shape) instead of carrying the old shape's over.
      delete query.settingVideo;
      try { sessionStorage.removeItem("selectedSettingVideo"); } catch (e) {}
      this.changeInMetalLoading = true;
      return Promise.resolve(this.$router.replace({ query: query }))
        .catch(function () { /* a duplicate-navigation rejection is harmless */ })
        .then(function () { return self.getSettingData(variant.id, { keepCurrentOnFailure: true }); })
        .then(function () { self.refreshChildSettingCopies(); })
        .finally(function () { self.changeInMetalLoading = false; });
    },
    /* FinalRingMetaData / FinalRingDetailTable snapshot sessionStorage's
       selectedSetting once in data() (cart-conflict modal, spec rows); point
       them at the swapped setting so they don't keep the old variant's name. */
    refreshChildSettingCopies() {
      var fresh = safeParseJSON("selectedSetting");
      if (!fresh) return;
      [this.$refs.metaData, this.$refs.metaDataTablet, this.$refs.detailTable].forEach(function (ref) {
        if (ref) ref.selectedSetting = fresh;
      });
    },
    /* The product's full variant list — from this setting (and its sessionStorage
       mirror) when an earlier resolve already stored it there, else fetched once. */
    getSettingSiblingVariants(settingId) {
      var cached = this.setting.data && this.setting.data.shapeVariants;
      if (Array.isArray(cached) && cached.some(function (v) { return String(v.id) === String(settingId); })) {
        return Promise.resolve(cached);
      }
      if (this._settingSiblingsFetch && this._settingSiblingsFetch.settingId === String(settingId)) {
        return this._settingSiblingsFetch.promise;
      }
      var self = this;
      var promise = this.fetchSettingSiblingVariants(settingId).then(function (variants) {
        if (variants.length && self.setting.data && String(self.setting.data.id) === String(settingId)) {
          self.setting.data.shapeVariants = variants;
          var stored = safeParseJSON("selectedSetting");
          if (stored && String(stored.id) === String(settingId)) {
            stored.shapeVariants = variants;
            try { sessionStorage.setItem("selectedSetting", JSON.stringify(stored)); } catch (e) {}
          }
        }
        return variants;
      });
      this._settingSiblingsFetch = { settingId: String(settingId), promise: promise };
      return promise;
    },
    /* Every variant of the product this setting variant belongs to, as
       { id, sku, option1 (metal), option2 (shape), image }. Same no-token
       Storefront endpoint getSettingData() uses; paginates in case a ring's
       metal × shape matrix ever exceeds one page. */
    async fetchSettingSiblingVariants(settingId) {
      var variants = [];
      var cursor = null;
      for (var page = 0; page < 5; page++) {
        var response = await axios.post(
          "/api/2026-04/graphql",
          {
            query: `
              query getSettingSiblings($id: ID!, $cursor: String) {
                node(id: $id) {
                  ... on ProductVariant {
                    product {
                      variants(first: 100, after: $cursor) {
                        pageInfo { hasNextPage endCursor }
                        nodes {
                          id
                          sku
                          selectedOptions { name value }
                          image { url }
                        }
                      }
                    }
                  }
                }
              }
            `,
            variables: { id: `gid://shopify/ProductVariant/${settingId}`, cursor: cursor },
          },
          { headers: { "Content-Type": "application/json" } }
        );
        var connection = response?.data?.data?.node?.product?.variants;
        if (!connection) break;
        (connection.nodes || []).forEach(function (v) {
          variants.push({
            id: Number(String(v.id).split("/").pop()),
            sku: v.sku || "",
            option1: v.selectedOptions?.[0]?.value || "",
            option2: v.selectedOptions?.[1]?.value || "",
            image: v.image?.url || null,
          });
        });
        if (!connection.pageInfo?.hasNextPage) break;
        cursor = connection.pageInfo.endCursor;
      }
      return variants;
    },
    handleBreadcrumbs() {
      const ringName = this.dataNotFound
        ? "No Ring Found"
        : `${this.setting.data.name} With ${getDiamondTitle(
          this.diamond.data
        )}`;
      // The dedup key below is the stable identity of this ring configuration
      // (settingId/diamondId/ringSize/etc.), independent of the actual link —
      // buildShareableFinalRingUrl() re-encodes the full setting/diamond objects
      // on every call, which can produce a slightly different ?data= string each
      // time handleBreadcrumbs() runs (e.g. before vs after data finishes
      // loading); keying the dedup check off that would duplicate this entry.
      const dedupKey = new URLSearchParams({
        settingId: this.$route.query.settingId || "",
        diamondId: this.$route.query.diamondId || "",
        diamondType: this.$route.query.diamondType || "",
      });
      if (this.finalSettingSize && this.finalSettingSize !== "Select Ring Size") {
        dedupKey.set("ringSize", this.finalSettingSize);
      }
      if (this.setting?.settingMetal) {
        dedupKey.set("settingMetal", this.setting.settingMetal);
      }
      const breadcrumbMatchingBandRaw = sessionStorage.getItem("selectedMatchingBand");
      if (breadcrumbMatchingBandRaw) {
        try {
          const breadcrumbMatchingBandId = JSON.parse(breadcrumbMatchingBandRaw)?.id;
          if (breadcrumbMatchingBandId) dedupKey.set("matchingBandId", breadcrumbMatchingBandId);
        } catch (e) {
          /* ignore */
        }
      }
      const diamondIdUrl = {
        path: `/collections/final-ring?${this.buildShareableFinalRingUrl()}`,
        name: ringName,
        dedupKey: dedupKey.toString(),
      };
      !this.breadcrumbs.some(
        (item) =>
          item.dedupKey === diamondIdUrl.dedupKey && item.name === diamondIdUrl.name
      ) && this.breadcrumbs.push(diamondIdUrl);
    },
    /* Builds a final-ring URL carrying the user's live ring-size/metal choices
       alongside the setting/diamond ids, so a link opened cold (QR scan, copied
       URL, another device) reproduces the same configuration instead of falling
       back to the setting's raw defaults. */
    buildShareableFinalRingUrl() {
      const params = new URLSearchParams();
      if (this.setting?.data?.name && this.diamond?.data?.diamondId) {
        let matchingBandId = null;
        try {
          matchingBandId = JSON.parse(sessionStorage.getItem("selectedMatchingBand") || "null")?.id || null;
        } catch (e) {
          /* ignore */
        }
        let secondDiamond = null;
        try {
          secondDiamond = JSON.parse(sessionStorage.getItem("selectedSecondDiamond") || "null");
        } catch (e) {
          /* ignore */
        }
        const fullPayloadEncoded = encodeRingData({
          setting: {
            ...trimSettingForUrl(this.setting.data),
            settingSize: this.finalSettingSize,
            settingMetal: this.setting.settingMetal,
            settingVideo: sessionStorage.getItem("selectedSettingVideo") || this.setting.data.settingVideo || null,
          },
          diamond: this.diamond.data,
          matchingBandId: matchingBandId,
          secondDiamond: secondDiamond?.diamondId
            ? { diamondId: secondDiamond.diamondId, diamondType: secondDiamond.diamondType || "" }
            : null,
        });
        if (fullPayloadEncoded) {
          params.set("data", fullPayloadEncoded);
        }
      }
      return params.toString();
    },
    getPageUrl() {
      return `${baseUrl}/collections/final-ring?${this.buildShareableFinalRingUrl()}`;
    },
    getMetaData(type) {
      var setting = this.setting.data;
      var diamond = this.diamond.data;
      var diamondType = this.$route.query.diamondType;
      var diamondTitle = getDiamondTitle(diamond, diamondType);
      switch (type) {
        case "description":
          return `Shop the ${setting.name} paired with a ${diamondTitle}. Customise your engagement ring with free shipping across Australia.`;
        case "title":
          let title;
          if (this.dataNotFound && !this.showSoldOutPopup && !this.soldOutCheckPending && !this.settingDiamondFetchPending) {
            // Named route (same as the "Start with setting" button): pushing the raw
            // "/collections/..." path breaks under the router's /pages/ base.
            this.$router.push({ name: "chooseSetting", query: { pageType: "setting" } });
            title = "No Ring Found";
          } else if (this.dataNotFound) {
            title = "No Ring Found";
          } else {
            title = `${setting.name} With ${diamondTitle}`;
          }
          return title;
        case "imgUrl":
          return setting.main_image;
        case "siteName":
          return "Vue Woocomerce";
        case "type":
          return "product";
        case "category":
          return "Engagement Ring";
        case "price":
          return parseFloat(diamond.finalPrice) + parseFloat(setting.price);
        case "currency":
          return diamond.currencyCode;
        case "availability":
          return setting.stock_status == "instock"
            ? "in stock"
            : "out of stock";
        case "googleCategory":
          return "Apparel & Accessories > Jewelry > Rings";
      }
    },
    handleMetaData() {
      document.title = this.getMetaData("title");
      /* set default and openGraph meta data for filter page */
      const meta = [
        { name: "description", content: this.getMetaData("description") },
        { property: "og:title", content: this.getMetaData("title") },
        { property: "og:site_name", content: "Vue Woocomerce" },
        { property: "og:type", content: "website" },
        { property: "og:url", content: this.getPageUrl() },
        { property: "og:image", content: this.getMetaData("imgUrl") },
        {
          property: "og:description",
          content: this.getMetaData("description"),
        },
        { property: "og:category", content: this.getMetaData("category") },
        { property: "og:price:amount", content: this.getMetaData("price") },
        {
          property: "og:price:currency",
          content: this.getMetaData("currency"),
        },
        {
          property: "og:availability",
          content: this.getMetaData("availability"),
        },
        {
          property: "og:google_product_category",
          content: this.getMetaData("googleCategory"),
        },
      ];
      const link = [{ rel: "canonical", href: this.getPageUrl() }];
      setMetaData("meta", meta);
      setMetaData("link", link);
    },
  },
  beforeRouteLeave(to, from) {
    document.querySelector("#site-wrapper").classList.remove("pc-relative");
  },
  beforeMount() {
    document.getElementById("initial-page-loader").style.display = "none";
    let elements = document.getElementsByClassName("js-recently-view");
    for (let i = 0; i < elements.length; i++) {
      elements[i].style.display = "block";
    }
  },
};
